import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { HiArrowCircleLeft, HiOutlineLocationMarker } from "react-icons/hi";
import { useFrontendQuestionnaire } from "../hooks/useFrontendQuestionnaire";
import clientConfigService from "../services/clientConfig";
import LoadingSpinner from "./LoadingSpinner";
import ErrorCard from "./ErrorCard";
import { blockedTreatmentAction } from "../features/treatment-flow/blockedTreatment";
import { FaInfoCircle } from "react-icons/fa";
import GroupedQuestion from "./QuestionTypes/GroupedQuestion";
import {
  createPreviewPatientPrefill,
  previewPatientDefaults,
  previewStateRoutingProjection,
  resolveTreatmentOrderSummary,
} from "../features/treatment-flow/previewSimulation";
import TreatmentJourneyChrome from "../features/treatment-flow/TreatmentJourneyChrome";
import {
  presentationForRuntimeQuestion,
  runtimeProgressBarState,
} from "../features/treatment-flow/runtimePresentation";
import {
  questionProgressIdentity,
  createOverallQuestionProgressHistory,
  possibleTreatmentQuestionCount,
  updateOverallQuestionProgress,
} from "../features/treatment-flow/overallQuestionProgress";
import TreatmentLabCheckoutStep from "../features/treatment-flow/TreatmentLabCheckoutStep";
import { visibleProgramLabCheckout } from "../features/treatment-flow/programLabVisibility";
import {
  hasProgramLabAoeQuestions,
  getProgramLabCollectionError,
  isProgramLabAoeComplete,
  isProgramLabAddressVerified,
  isProgramLabConsentsComplete,
  normalizeProgramLabCheckout,
  requiresProgramLabCheckout,
  verifyProgramLabAddress,
} from "../features/treatment-flow/programLabCheckout";
import { verifyPatientAddress } from "../features/labs/services/patientAddress";
import {
  activeJourneyStep,
  buildTerminalJourneyStep,
} from "../features/treatment-flow/journeyProjection";
import {
  MISSING_ANSWER_MESSAGE,
  planStepTransition,
  previousJourneyAuthorityState,
  previousRuntimeJourneyStep,
  previousVisibleAuthorityState,
  resolveMissingAnswerTarget,
  resolveCareLocationTarget,
} from "../features/treatment-flow/runtimeBoundaryNavigation";
import {
  CARE_LOCATION_STATUS,
  careLocationMessage,
  selectCareLocation,
} from "../features/treatment-flow/careLocationAdvance";
import { useJourneyScrollRestoration } from "../features/treatment-flow/useJourneyScrollRestoration";
import { useJourneyStateMachine } from "../features/treatment-flow/useJourneyStateMachine";
import { createJourneyBoundary } from "../features/treatment-flow/journeyBoundary";
import {
  usePreviewIdentity,
} from "../features/treatment-flow/previewIdentity";
import { PREVIEW_PROTOCOL } from "../features/treatment-flow/previewProtocolConstants";
import UnsupportedJourneyItem from "../features/treatment-flow/UnsupportedJourneyItem";
import { JOURNEY_SUPPORTED_QUESTION_TYPES } from "../features/treatment-flow/journeyConstants";
import {
  authorityStateForQuestion,
} from "../features/treatment-flow/authorityProgress";
import {
  backTargetForPreviousTreatmentStep,
  lastCustomProgramStepItemId,
} from "../features/treatment-flow/customProgramNavigation";
import {
  RUNTIME_PATIENT_AUTH_REQUIRED,
  TREATMENT_RUNTIME_STATE,
  TREATMENT_RUNTIME_STATE_ORDER,
} from "../features/treatment-flow/treatmentApiConstants";
import {
  FINAL_TREATMENT_STEP_ACTION,
  finalTreatmentStepAction,
  shouldCompleteLegacyProductSelection,
} from "../features/treatment-flow/finalStepNavigation";
import PreviewPaymentFields from "../features/treatment-flow/PreviewPaymentFields";
import TextInput from "./QuestionTypes/TextInput";
import FileUpload from "./QuestionTypes/FileUpload";
import ConsentForm from "./QuestionTypes/ConsentForm";
import CheckoutQuestion from "./QuestionTypes/CheckoutQuestion";
import SingleChoice from "./QuestionTypes/SingleChoice";
import MatchedProgramsQuestion, {
  customProgramSelectionError,
} from "../features/treatment-flow/MatchedProgramsQuestion";
import TreatmentScreeningIntro from "../features/treatment-flow/TreatmentScreeningIntro";
import TreatmentProductSelection from "../features/treatment-flow/TreatmentProductSelection";
import TreatmentCheckoutRecovery from "../features/treatment-flow/TreatmentCheckoutRecovery";
import TreatmentCheckoutReview from "../features/treatment-flow/TreatmentCheckoutReview";
import TreatmentOrderSubmitted from "../features/treatment-flow/TreatmentOrderSubmitted";
import TreatmentRuntimeNotice from "../features/treatment-flow/TreatmentRuntimeNotice";
import EarlierIntakeChoice from "../features/treatment-flow/EarlierIntakeChoice";
import AddressSuggestionDialog from "../features/treatment-flow/AddressSuggestionDialog";
import TreatmentAuthenticationStep from "../features/treatment-flow/authentication/TreatmentAuthenticationStep";
import { AUTH_BIND_DISPOSITION } from "../features/treatment-flow/authentication/authenticationConstants";
import { fetchPatientProfile } from "../features/treatment-flow/authentication/authenticationApi";
import { completeResumeSignIn } from "../features/treatment-flow/authentication/resumeSignIn";
import { LAUNCH_REASON } from "../features/treatment-flow/launchReason";
import { useDifferentAccountNotice } from "../features/treatment-flow/authentication/useDifferentAccountNotice";
import {
  claimAuthenticationAdvance,
  completePostBindAuthority,
  firstPostAuthenticationRoutingQuestion,
  trackAuthenticationStepVisit,
} from "../features/treatment-flow/authentication/postBindAuthority";
import {
  clearProgramAuthentication,
  loadProgramAuthentication,
  storeProgramAuthentication,
  updateProgramAuthenticationProfile,
} from "../features/treatment-flow/authentication/programAuthenticationStorage";
import {
  categoryHasMultipleRequiredCards,
  isAutoIncludedRequiredCompanion,
  isProductSelectionComplete,
  groupedProductSelectionContract,
  requiredSupplyGroups,
  requiredSupplyOptionFor,
  partitionProductSelectionsByQuestion,
  productSelectionChoiceKey,
  productSelectionCategoryErrorAcrossQuestions,
  productSelectionError,
  productSelectionGroupBounds,
} from "../features/treatment-flow/checkoutContract";
import {
  buildTreatmentOrdersUrl,
} from "../features/treatment-flow/patientPortalUrl";
import {
  TREATMENT_CHECKOUT_COPY,
  TREATMENT_TERMINAL_STEP,
} from "../features/treatment-flow/treatmentUiConstants";
import {
  collectGroupedProductSelections,
} from "../features/treatment-flow/productSelectionResponse";
import StateRouting from "./QuestionTypes/StateRouting";
import MultipleChoice from "./QuestionTypes/MultipleChoice";
import NumberInput from "./QuestionTypes/NumberInput";
import PaymentStep from "./QuestionTypes/PaymentStep";
import HeightWeight from "./QuestionTypes/HeightWeight";
import MedicationDoseSelector from "./QuestionTypes/MedicationDoseSelector";
import PhoneNumberInput from "./QuestionTypes/PhoneNumberInput";
import WelcomePage from "./WelcomePage";
import ExistingPatientLoginModal from "./ExistingPatientLoginModal";
import api, { getApiErrorMessage } from "../services/api";
import followUpService from "../services/followUp";
import authService from "../services/authService";
import {
  pickQuestionnaireLogoUrl,
  resolveQuestionnaireBranding,
} from "../utils/questionnaireBranding";
import { US_STATE_CODE_BY_NAME } from "../constants/usStates";
import { PATIENT_AUTH_ERROR_CODES } from "../constants/authErrors";
import { US_FLAG_SRC } from "../constants/usFlag";
import {
  isRuntimePatientPermissionError,
  patientIdentityKey,
  patientIdentityMatchesEmail,
  samePatientIdentity,
} from "../features/treatment-flow/patientRuntimeIdentity";
import { runtimeDisqualificationActionLabel } from "../features/treatment-flow/runtimeDisqualification";
import { buildCompletionResponseSnapshot } from "../features/treatment-flow/completionResponseSnapshot";

// States the runtime may legitimately hold once an answer is accepted and the
// journey should still move to its next rendered step. Anything still walking
// the ordered journey qualifies: a single-treatment journey auto-selects its
// one Program and lands on treatment screening, a multi-treatment journey
// stops at potential_matches to let the patient pick, and answering a
// treatment's last question lands on consents.
//
// Derived from the order map rather than listed by hand. An allowlist stalled
// the cursor once per state it forgot -- first potential_matches, then
// consents -- and a new mid-journey state would have stalled it again.
// Checkout and the terminal states (disqualified, expired, completed) carry no
// journey position, so `has` rejects them and their own branches still run.
const ADVANCEABLE_POST_ANSWER_STATES = new Set(
  Object.keys(TREATMENT_RUNTIME_STATE_ORDER).filter((state) => (
    TREATMENT_RUNTIME_STATE_ORDER[state]
    < TREATMENT_RUNTIME_STATE_ORDER[TREATMENT_RUNTIME_STATE.checkoutReady]
  )),
);

const redirectToTreatmentCheckout = (checkout, patientInfo, branding, clientId) => {
  const capability = checkout?.checkout_capability;
  if (!capability) throw new Error("Secure treatment checkout is not ready.");
  const durableCheckoutUrl = String(checkout?.checkout_url || "").trim();
  let token = String(
    checkout.session_id
    || checkout.runtime_session_id
    || capability.slice(0, 32),
  ).replace(/[^a-zA-Z0-9_-]/g, "_");
  if (durableCheckoutUrl) {
    try {
      const checkoutPath = new URL(durableCheckoutUrl, window.location.origin)
        .pathname.split("/").filter(Boolean);
      token = checkoutPath[checkoutPath.indexOf("checkout") + 1] || token;
    } catch (_error) {
      // Keep the compatibility fallback for older API responses.
    }
  }
  sessionStorage.setItem(
    `welliemd_treatment_checkout:${token}`,
    JSON.stringify({
      ...checkout,
      client_id: checkout.client_id || clientId,
      questionnaire_branding: branding || checkout.questionnaire_branding,
      client_idempotency_key: checkout.client_idempotency_key
        || globalThis.crypto?.randomUUID?.()
        || `checkout-${token}`,
      patient_name: patientInfo?.name
        || [patientInfo?.firstName, patientInfo?.lastName].filter(Boolean).join(" "),
    }),
  );
  window.location.assign(
    durableCheckoutUrl || `/checkout/${encodeURIComponent(token)}`,
  );
};

const SIMPLE_PREFILLABLE_TYPES = {
  sex: (pd) => pd?.personal_details?.sex,
  medical_conditions: (pd) => pd?.medical_info?.medical_conditions,
  self_reported_meds: (pd) => pd?.medical_info?.self_reported_meds,
  allergies: (pd) => pd?.medical_info?.allergies,
};

const evaluateProgramVisibility = (rules, responses) => {
  if (!rules || typeof rules !== "object") return true;
  const resolveResponseValue = (questionId) => {
    if (!questionId) return undefined;
    if (Object.prototype.hasOwnProperty.call(responses || {}, questionId)) {
      return responses[questionId];
    }
    const normalizedQuestionId = String(questionId);
    const matchedKey = Object.keys(responses || {}).find(
      (key) => key === normalizedQuestionId || key.endsWith(`-${normalizedQuestionId}`)
    );
    return matchedKey ? responses[matchedKey] : undefined;
  };
  const compare = (actual, operator, expected) => {
    switch (operator) {
      case "equals":
      case "eq":
        return String(actual) === String(expected);
      case "not_equals":
      case "neq":
        return String(actual) !== String(expected);
      case "contains":
        return Array.isArray(actual)
          ? actual.map(String).includes(String(expected))
          : String(actual || "").includes(String(expected));
      case "greater_than":
      case "gt":
        return Number(actual) > Number(expected);
      case "greater_than_or_equal":
      case "gte":
        return Number(actual) >= Number(expected);
      case "less_than":
      case "lt":
        return Number(actual) < Number(expected);
      case "less_than_or_equal":
      case "lte":
        return Number(actual) <= Number(expected);
      default:
        return true;
    }
  };
  const evaluateNode = (node) => {
    if (!node) return true;
    if (node.mode && (Array.isArray(node.rules) || Array.isArray(node.subgroups))) {
      const children = [
        ...(Array.isArray(node.rules) ? node.rules : []),
        ...(Array.isArray(node.subgroups) ? node.subgroups : []),
      ];
      if (!children.length) return true;
      const method = String(node.operator || (node.mode === "simple" ? "OR" : "AND")).toUpperCase() === "OR"
        ? "some"
        : "every";
      return children[method](evaluateNode);
    }
    const children = node.children || node.rules;
    if (Array.isArray(children)) {
      const method = String(node.operator || node.logic || "AND").toUpperCase() === "OR"
        ? "some"
        : "every";
      return children[method](evaluateNode);
    }
    const questionId = node.questionId || node.question_id;
    if (!questionId) return true;
    return compare(
      resolveResponseValue(questionId),
      node.operator || node.comparison,
      node.value
    );
  };
  if (rules.show_if) return evaluateNode(rules.show_if);
  if (rules.skip_if) return !evaluateNode(rules.skip_if);
  return evaluateNode(rules);
};

const hasProgramRuleContent = (rules) =>
  Boolean(rules && typeof rules === "object" && Object.keys(rules).length > 0);

const firstProgramRuleSet = (...candidates) =>
  candidates.find((rules) => hasProgramRuleContent(rules)) || {};

const authInputStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  boxSizing: "border-box",
  fontSize: "16px",
  padding: "12px 14px",
  width: "100%",
};

const ProgramAuthenticationPreview = ({
  question,
  value,
  onChange,
  error,
  previewOnly,
  previewIdentity,
}) => {
  const config = question.auth_config || {};
  const steps = [
    config.email && "email",
    config.phone && "phone",
    config.identity && !question.identity_question_separate && "identity",
    config.account && "account",
  ].filter(Boolean);
  const [stepIndex, setStepIndex] = useState(0);
  const [showCode, setShowCode] = useState(false);
  const [form, setForm] = useState({
    email: "",
    emailCode: "",
    phone: "",
    phoneCode: "",
    identityFile: null,
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    acceptedTerms: false,
  });
  const [localError, setLocalError] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const activeStep = steps[stepIndex];
  const existingPatientMode =
    previewOnly &&
    previewIdentity === PREVIEW_PROTOCOL.identities.existingPatient;

  useEffect(() => {
    if (!existingPatientMode && steps.length === 0 && !value?.completed) {
      onChange({ completed: true, preview: true });
    }
  }, [existingPatientMode, onChange, steps.length, value?.completed]);

  const updateField = (field, nextValue) => {
    setForm((current) => ({ ...current, [field]: nextValue }));
    setLocalError("");
  };

  const finishStep = (formPatch = {}) => {
    const completedForm = { ...form, ...formPatch };
    setShowCode(false);
    setLocalError("");
    if (stepIndex < steps.length - 1) {
      setStepIndex((current) => current + 1);
      return;
    }
    onChange({
      completed: true,
      preview: Boolean(previewOnly),
      methods: steps,
      email: completedForm.email,
      phone: completedForm.phone,
      firstName: completedForm.firstName,
      lastName: completedForm.lastName,
      emailVerificationToken: completedForm.emailVerificationToken || null,
      password: completedForm.password || null,
    });
  };

  const handleEmail = async () => {
    if (!showCode) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        setLocalError("Enter a valid email address.");
        return;
      }
      setProcessing(true);
      try {
        if (!previewOnly) {
          const result = await authService.sendEmailVerificationCode(form.email);
          setChallengeId(result.challenge_id);
        }
        setShowCode(true);
      } catch (sendError) {
        setLocalError(sendError.message);
      } finally {
        setProcessing(false);
      }
      return;
    }
    if (!/^\d{6}$/.test(form.emailCode)) {
      setLocalError("Enter the 6-digit verification code.");
      return;
    }
    setProcessing(true);
    try {
      let verification = {};
      if (!previewOnly) {
        verification = await authService.verifyEmailCode(challengeId, form.emailCode);
      }
      const verificationPatch = {
        emailVerificationToken: verification.verification_token || null,
      };
      setForm((current) => ({ ...current, ...verificationPatch }));
      finishStep(verificationPatch);
    } catch (verifyError) {
      setLocalError(verifyError.message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePhone = async () => {
    if (!showCode) {
      if (form.phone.replace(/\D/g, "").length < 10) {
        setLocalError("Enter a valid phone number.");
        return;
      }
      setProcessing(true);
      try {
        if (!previewOnly) {
          const result = await authService.sendPhoneVerificationCode(form.phone);
          setChallengeId(result.challenge_id);
        }
        setShowCode(true);
      } catch (sendError) {
        setLocalError(sendError.message);
      } finally {
        setProcessing(false);
      }
      return;
    }
    if (!/^\d{6}$/.test(form.phoneCode)) {
      setLocalError("Enter the 6-digit verification code.");
      return;
    }
    setProcessing(true);
    try {
      if (!previewOnly) {
        await authService.verifyPhoneCode(challengeId, form.phoneCode);
      }
      finishStep();
    } catch (verifyError) {
      setLocalError(verifyError.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleAccount = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setLocalError("Enter your first and last name.");
      return;
    }
    if (!config.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setLocalError("Enter a valid email address.");
      return;
    }
    if (!config.phone && form.phone.replace(/\D/g, "").length < 10) {
      setLocalError("Enter a valid phone number.");
      return;
    }
    if (form.password.length < 8) {
      setLocalError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setLocalError("Passwords do not match.");
      return;
    }
    if (!form.acceptedTerms) {
      setLocalError("You must agree to the Terms, Privacy Policy, and Telehealth consent.");
      return;
    }
    finishStep();
  };

  if (value?.completed && (previewOnly || value.email || value.phone)) {
    return (
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", color: "#166534", marginTop: "20px", padding: "16px" }}>
        Authentication complete
      </div>
    );
  }

  const actionStyle = {
    background: "#222222",
    border: 0,
    borderRadius: "999px",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: 600,
    marginTop: "18px",
    padding: "12px 24px",
    width: "100%",
  };

  if (existingPatientMode) {
    const completeExistingPatientLogin = () => {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        setLocalError("Enter a valid email address.");
        return;
      }
      if (!form.password) {
        setLocalError("Enter a password to continue.");
        return;
      }
      onChange({
        completed: true,
        preview: true,
        existing_patient: true,
        email: form.email,
      });
    };

    return (
      <div style={{ marginTop: "20px" }}>
        <h3 style={{ fontSize: "22px", margin: "0 0 8px" }}>Welcome back</h3>
        <p style={{ color: "#64748b", margin: "0 0 20px" }}>
          Simulate an existing patient login without looking up or changing a patient record.
        </p>
        <label htmlFor="program-preview-existing-email" style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}>
          Email address
        </label>
        <input
          id="program-preview-existing-email"
          onChange={(event) => updateField("email", event.target.value)}
          placeholder="patient@example.com"
          style={authInputStyle}
          type="email"
          value={form.email}
        />
        <label htmlFor="program-preview-existing-password" style={{ display: "block", fontWeight: 600, margin: "18px 0 8px" }}>
          Password
        </label>
        <input
          id="program-preview-existing-password"
          onChange={(event) => updateField("password", event.target.value)}
          placeholder="Preview password"
          style={authInputStyle}
          type="password"
          value={form.password}
        />
        <button onClick={completeExistingPatientLogin} style={actionStyle} type="button">
          Log in as existing patient
        </button>
        {(localError || error) && (
          <div style={{ color: "#dc2626", marginTop: "12px" }}>{localError || error}</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: "20px" }}>
      {activeStep === "email" && (
        <>
          <label htmlFor="program-preview-email" style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}>
            {showCode ? "Verification code" : "Email address"}
          </label>
          <input
            id="program-preview-email"
            inputMode={showCode ? "numeric" : "email"}
            maxLength={showCode ? 6 : undefined}
            onChange={(event) => updateField(showCode ? "emailCode" : "email", event.target.value)}
            placeholder={showCode ? "Enter 6-digit code" : "you@example.com"}
            style={authInputStyle}
            type={showCode ? "text" : "email"}
            value={showCode ? form.emailCode : form.email}
          />
          <button disabled={processing} onClick={handleEmail} style={actionStyle} type="button">
            {processing ? "Please wait..." : showCode ? "Verify email" : "Send verification code"}
          </button>
        </>
      )}

      {activeStep === "phone" && (
        <>
          <label htmlFor="program-preview-phone" style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}>
            {showCode ? "SMS verification code" : "Mobile phone number"}
          </label>
          <span style={{ display: "flex" }}>
            {!showCode && (
              <span style={{ alignItems: "center", background: "#f8fafc", border: "1px solid #cbd5e1", borderRight: 0, borderRadius: "8px 0 0 8px", display: "inline-flex", gap: "6px", padding: "0 10px" }}>
                <img src={US_FLAG_SRC} alt="United States flag" width="22" height="14" style={{ borderRadius: "2px", display: "block" }} />
                <span>+1</span>
              </span>
            )}
            <input
              id="program-preview-phone"
              inputMode={showCode ? "numeric" : "tel"}
              maxLength={showCode ? 6 : undefined}
              onChange={(event) => updateField(showCode ? "phoneCode" : "phone", event.target.value)}
              placeholder={showCode ? "Enter 6-digit code" : "(555) 555-5555"}
              style={{ ...authInputStyle, ...(showCode ? {} : { borderRadius: "0 8px 8px 0", minWidth: 0 }) }}
              type="tel"
              value={showCode ? form.phoneCode : form.phone}
            />
          </span>
          <button disabled={processing} onClick={handlePhone} style={actionStyle} type="button">
            {processing ? "Please wait..." : showCode ? "Verify phone" : "Send SMS code"}
          </button>
        </>
      )}

      {activeStep === "identity" && (
        <>
          <label htmlFor="program-preview-identity" style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}>
            Government-issued photo ID
          </label>
          <input
            accept="image/jpeg,image/png,image/heic,application/pdf"
            id="program-preview-identity"
            onChange={(event) => updateField("identityFile", event.target.files?.[0] || null)}
            style={authInputStyle}
            type="file"
          />
          <button
            onClick={() => form.identityFile ? finishStep() : setLocalError("Choose an ID document to continue.")}
            style={actionStyle}
            type="button"
          >
            Continue
          </button>
        </>
      )}

      {activeStep === "account" && (
        <>
          <h3 style={{ fontSize: "24px", margin: "0 0 22px" }}>Personal Details</h3>
          <div style={{ display: "grid", gap: "22px 16px", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
            <label style={{ display: "grid", gap: "8px", fontWeight: 600 }}>
              <span>First Name <span style={{ color: "#dc2626" }}>*</span></span>
              <input onChange={(event) => updateField("firstName", event.target.value)} placeholder="Your Response" style={authInputStyle} value={form.firstName} />
            </label>
            <label style={{ display: "grid", gap: "8px", fontWeight: 600 }}>
              <span>Last Name <span style={{ color: "#dc2626" }}>*</span></span>
              <input onChange={(event) => updateField("lastName", event.target.value)} placeholder="Your Response" style={authInputStyle} value={form.lastName} />
            </label>
            {!config.email && (
              <label style={{ display: "grid", gap: "8px", fontWeight: 600 }}>
                <span>Email Address <span style={{ color: "#dc2626" }}>*</span></span>
                <input onChange={(event) => updateField("email", event.target.value)} placeholder="your.email@example.com" style={authInputStyle} type="email" value={form.email} />
              </label>
            )}
            {!config.phone && (
              <label style={{ display: "grid", gap: "8px", fontWeight: 600 }}>
                <span>Phone Number <span style={{ color: "#dc2626" }}>*</span></span>
                <span style={{ display: "flex" }}>
                  <span style={{ alignItems: "center", background: "#f8fafc", border: "1px solid #cbd5e1", borderRight: 0, borderRadius: "8px 0 0 8px", display: "inline-flex", gap: "6px", padding: "0 10px" }}>
                    <img src={US_FLAG_SRC} alt="United States flag" width="22" height="14" style={{ borderRadius: "2px", display: "block" }} />
                    <span>+1</span>
                  </span>
                  <input
                    inputMode="tel"
                    onChange={(event) => updateField("phone", event.target.value)}
                    placeholder="(321) 123-4567"
                    style={{ ...authInputStyle, borderRadius: "0 8px 8px 0", minWidth: 0 }}
                    type="tel"
                    value={form.phone}
                  />
                </span>
              </label>
            )}
            <label style={{ display: "grid", gap: "8px", fontWeight: 600 }}>
              <span>Create Password <span style={{ color: "#dc2626" }}>*</span></span>
              <input onChange={(event) => updateField("password", event.target.value)} placeholder="At least 8 characters" style={authInputStyle} type="password" value={form.password} />
            </label>
            <label style={{ display: "grid", gap: "8px", fontWeight: 600 }}>
              <span>Confirm Password <span style={{ color: "#dc2626" }}>*</span></span>
              <input onChange={(event) => updateField("confirmPassword", event.target.value)} placeholder="Re-enter password" style={authInputStyle} type="password" value={form.confirmPassword} />
            </label>
          </div>
          <label style={{ alignItems: "flex-start", borderTop: "1px solid #e2e8f0", cursor: "pointer", display: "flex", gap: "12px", lineHeight: 1.5, marginTop: "28px", paddingTop: "22px" }}>
            <input
              checked={form.acceptedTerms}
              onChange={(event) => updateField("acceptedTerms", event.target.checked)}
              style={{ height: "20px", marginTop: "2px", width: "20px" }}
              type="checkbox"
            />
            <span>
              I agree to the <u>Terms</u> and <u>Privacy Policy</u> and consent to <u>Telehealth</u>.
            </span>
          </label>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button onClick={handleAccount} style={{ ...actionStyle, maxWidth: "240px" }} type="button">
              Create Account
            </button>
          </div>
          <div style={{ color: "#64748b", marginTop: "18px", textAlign: "center" }}>
            Already have an account? <button style={{ background: "none", border: 0, color: "#222222", cursor: "pointer", font: "inherit", fontWeight: 600, padding: 0, textDecoration: "underline" }} type="button">Log in</button>
          </div>
        </>
      )}

      {(localError || error) && (
        <div style={{ color: "#dc2626", marginTop: "12px" }}>{localError || error}</div>
      )}
    </div>
  );
};

const normalizeStateCode = (rawValue) => {
  if (!rawValue || typeof rawValue !== "string") return null;
  const trimmed = rawValue.trim();
  if (!trimmed) return null;

  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  const parenMatch = trimmed.match(/\(([A-Za-z]{2})\)$/);
  if (parenMatch) {
    return parenMatch[1].toUpperCase();
  }

  return US_STATE_CODE_BY_NAME[trimmed.toLowerCase()] || null;
};

const normalizeServiceStateCodes = (rawStates) => {
  if (!Array.isArray(rawStates) || rawStates.length === 0) return [];

  const seen = new Set();
  const normalized = [];

  rawStates.forEach((state) => {
    const code = normalizeStateCode(String(state || ""));
    if (!code || seen.has(code)) return;
    seen.add(code);
    normalized.push(code);
  });

  return normalized;
};

const getProductSelectionChoiceKey = (choice) => {
  return productSelectionChoiceKey(choice);
};

const dedupeProductSelectionItems = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = getProductSelectionChoiceKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const materializeRequiredProductSelections = (choices, selectedItems) => {
  const selected = dedupeProductSelectionItems(selectedItems || []);
  const selectedKeys = new Set(
    selected.map((item) => getProductSelectionChoiceKey(item)),
  );
  const requiredSupplyOptionIds = new Set(
    requiredSupplyGroups(choices).flatMap((group) => group
      .filter((choice) => choice?.product_role === "required_companion")
      .map((choice) => getProductSelectionChoiceKey(choice))
      .filter(Boolean)),
  );
  const requiredChoices = [
    // Several Required cards in one category are alternatives; the patient's
    // own choice satisfies them, so none is topped up here.
    ...requiredSupplyGroups(choices)
      .map((group) => requiredSupplyOptionFor(group, selected))
      .filter((choice) => !categoryHasMultipleRequiredCards(choice, choices)),
    ...(choices || []).filter((choice) => (
      isAutoIncludedRequiredCompanion(choice, choices)
      && !requiredSupplyOptionIds.has(getProductSelectionChoiceKey(choice))
    )),
  ];
  requiredChoices.forEach((choice) => {
    if (!choice) return;
    const key = getProductSelectionChoiceKey(choice);
    if (!key || selectedKeys.has(key)) return;
    selectedKeys.add(key);
    selected.push({
      ...choice,
      option_id: choice.option_id || choice.id || choice.value,
      value: choice.value || choice.option_id || choice.id,
      days_supply: Number(
        choice.default_supply_duration || choice.days_supply || 30,
      ),
      quantity: Number(choice.quantity || 1),
    });
  });
  return selected;
};

const getSelectedStateCode = (questionnaire, responses, treatmentJourney = false) => {
  if (!questionnaire?.questions || !responses) return null;

  const stateRoutingQuestion = questionnaire.questions.find(
    (q) => q.question_type === "state_routing"
  );
  if (stateRoutingQuestion) {
    const code = normalizeStateCode(responses[stateRoutingQuestion.id]);
    if (code) return code;
  }

  if (!treatmentJourney) {
    const shippingQuestion = questionnaire.questions.find(
      (q) => q.question_type === "shipping_address"
    );
    if (shippingQuestion) {
      const shippingResponse = responses[shippingQuestion.id];
      if (shippingResponse && typeof shippingResponse === "object") {
        const stateSubQuestion = (shippingQuestion.sub_questions || []).find(
          (sq) =>
            sq.question_type === "state" ||
            sq.question_text?.toLowerCase().includes("state")
        );
        if (stateSubQuestion) {
          const code = normalizeStateCode(shippingResponse[stateSubQuestion.id]);
          if (code) return code;
        }
        const codeFromStateKey = normalizeStateCode(shippingResponse.state);
        if (codeFromStateKey) return codeFromStateKey;
      }
    }
  }

  if (!treatmentJourney) {
    const standaloneStateQuestion = questionnaire.questions.find(
      (q) => q.question_type === "state"
    );
    if (standaloneStateQuestion) {
      return normalizeStateCode(responses[standaloneStateQuestion.id]);
    }
  }

  return null;
};

export const resolveAllowedRoutingPharmacyId = (
  routingRules,
  routingCategoryGroup,
  stateCode
) => {
  if (!routingRules || !routingCategoryGroup || !stateCode) return null;
  const groupRule = routingRules[routingCategoryGroup.toLowerCase()];
  if (!groupRule) return null;

  const matches = Array.isArray(groupRule.matches) ? groupRule.matches : [];
  const stateMatches = [];
  for (const match of matches) {
    const states = (match?.conditions?.state || [])
      .map((state) => String(state || "").toUpperCase())
      .filter(Boolean);
    if (states.includes(stateCode) && match?.pharmacy_id) {
      stateMatches.push(match);
    }
  }

  if (stateMatches.length > 0) {
    // Choose the highest-priority match (lowest numeric priority).
    // If priority is missing/invalid, keep relative order and treat as lower precedence.
    stateMatches.sort((a, b) => {
      const aPriority = Number.isFinite(Number(a?.priority))
        ? Number(a.priority)
        : Number.MAX_SAFE_INTEGER;
      const bPriority = Number.isFinite(Number(b?.priority))
        ? Number(b.priority)
        : Number.MAX_SAFE_INTEGER;
      return aPriority - bPriority;
    });
    return String(stateMatches[0].pharmacy_id);
  }

  if (groupRule.else_match?.pharmacy_id) {
    return String(groupRule.else_match.pharmacy_id);
  }
  return null;
};

export const inferRoutingCategoryGroupForChoice = (choice, routingRules) => {
  if (!choice || typeof choice !== "object" || !routingRules) return null;

  const ruleKeys = Object.keys(routingRules || {})
    .map((k) => String(k || "").trim().toLowerCase())
    .filter(Boolean);
  if (ruleKeys.length === 0) return null;

  const direct = String(choice.routing_category_group || "")
    .trim()
    .toLowerCase();
  if (direct && ruleKeys.includes(direct)) return direct;

  const candidateFields = [
    choice.routing_category_group,
    choice.category_group,
  ];

  const candidates = candidateFields
    .map((v) => String(v || "").trim().toLowerCase())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (ruleKeys.includes(candidate)) return candidate;
  }

  return null;
};

export const shouldDisplayChoiceForRouting = (
  choice,
  routingRules,
  selectedStateCode
) => {
  if (!choice || typeof choice !== "object") return true;
  if (!selectedStateCode) return true;

  const serviceStates = normalizeServiceStateCodes(choice.service_states);
  if (serviceStates.length > 0 && !serviceStates.includes(selectedStateCode)) {
    return false;
  }

  if (!routingRules) return true;

  const routingGroup = inferRoutingCategoryGroupForChoice(choice, routingRules);
  // If this choice does not belong to any routed group, leave it visible.
  // Routing should only constrain products whose category group has a rule.
  if (!routingGroup) return true;

  const allowedPharmacyId = resolveAllowedRoutingPharmacyId(
    routingRules,
    routingGroup,
    selectedStateCode
  );
  if (!allowedPharmacyId) return false;

  const choicePharmacyId = choice.pharmacy_id
    ? String(choice.pharmacy_id)
    : null;
  // Product-level service-state coverage can intentionally make a product
  // eligible without a pharmacy mapping. In that case, do not let legacy
  // pharmacy routing hide the product after service-state filtering passed.
  if (!choicePharmacyId) return true;

  return choicePharmacyId === allowedPharmacyId;
};

export const shouldDisplayChoiceForServiceState = (choice, selectedStateCode) => {
  if (!choice || typeof choice !== "object") return true;
  if (!selectedStateCode) return true;

  const serviceStates = Array.isArray(choice.service_states)
    ? choice.service_states
        .map((state) => String(state || "").trim().toUpperCase())
        .filter(Boolean)
    : [];

  // Empty effective service state list means unrestricted/all states.
  if (serviceStates.length === 0) return true;

  return serviceStates.includes(String(selectedStateCode).trim().toUpperCase());
};

/**
 * Build SingleChoice answer_choices from visit config medicines so product selection
 * always shows all products returned by the backend (e.g. 5ml and 10ml Glutathione).
 */
function buildProductChoicesFromMedicines(medicines) {
  if (!Array.isArray(medicines) || medicines.length === 0) return [];
  const choices = [];

  medicines
    .filter((m) => m && m.is_active !== false && m.product_type !== "supply" && m.kind !== "supply" && !m.is_supply)
    .forEach((m) => {
      if (Array.isArray(m.variants) && m.variants.length > 0) {
        m.variants
          .filter((v) => v.is_active !== false)
          .forEach((v) => {
            choices.push({
              product_id: m.id,
              product_name: m.name,
              product_image: m.product_image || "",
              id: v.id,
              name: v.name || m.name,
              pharmacy_id: m.pharmacy_id ? String(m.pharmacy_id) : null,
              service_states: normalizeServiceStateCodes(m.service_states),
              routing_category_group: m.routing_category_group || null,
              is_active: true,
              variant_id: v.id,
              quantity: v.quantity,
              final_price: v.final_price,
              original_price:
                v.total_price !== v.final_price ? v.total_price : undefined,
              total: v.total_price,
              shipping_fee: v.shipping_fee,
              price: v.final_price,
              days_supply: v.quantity,
              refills: v.refills ?? m.refills,
            });
          });
      } else {
        choices.push({
          product_id: m.id,
          product_name: m.name,
          product_image: m.product_image || "",
          id: m.id,
          name: m.name,
          pharmacy_id: m.pharmacy_id ? String(m.pharmacy_id) : null,
          service_states: normalizeServiceStateCodes(m.service_states),
          routing_category_group: m.routing_category_group || null,
          is_active: true,
          refills: m.refills,
        });
      }
    });

  return choices;
}

// ===== ANIMATIONS =====
const slideIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// ===== STYLED COMPONENTS =====
const Container = styled.div`
  &.legacy-questionnaire {
    /* Match the production legacy questionnaire theme without overriding
       tenant branding used by the new Program journeys. */
    --zyvgo-primary: #222222;
    --zyvgo-accent: #f4d2b1;
  }

  max-width: 500px;
  margin: 0 auto;
  padding: 0;
  font-family: var(
    --zyvgo-font-family,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif
  );
  background-color: transparent;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

// Fixed header section that stays at top
const HeaderSection = styled.div`
  position: sticky;
  top: 0;
  background-color: #ffffff;
  z-index: 100;
  padding-top: 40px;

  @media (max-width: 768px) {
    padding-top: 20px;
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 7px;
  background-color: #e5e7eb;
  position: static;
  top: 40px;
  left: 0;
  z-index: 100;
  border-radius: 10px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: var(--zyvgo-accent, #f4d2b1);
  transition: width 0.3s ease;
  width: ${(props) => props.width}%;
  border-radius: 10px;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0px;
  max-width: 500px;
  margin: 0;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: transparent;
  border: 2px solid var(--zyvgo-border, #e1e5e9);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--zyvgo-text-primary, #222222);
  font-size: 18px;
  padding: 0;

  &:hover:not(:disabled) {
    border-color: var(--zyvgo-primary, #222222);
    background-color: rgba(34, 34, 34, 0.05);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Logo = styled.div`
  svg,
  img {
    height: 32px;
    width: auto;
    display: block;
  }
`;

const CompanyNameText = styled.span`
  font-family: var(
    --zyvgo-font-family,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif
  );
  font-size: 24px;
  font-weight: 700;
  color: var(--zyvgo-primary, #222222);
  white-space: nowrap;
`;

// Capability previews begin with a neutral shell while the server resolves
// whether the preview is tenant-branded or an internal WellieMD preview.
// Retain the header dimensions without briefly exposing the provisional brand.
const BrandPlaceholder = styled.span`
  display: block;
  width: 120px;
  height: 32px;
`;

const ContentWrapper = styled.div`
  padding: 40px 0px 80px;
  max-width: 500px;
  margin: 0;
  animation: ${slideIn} 0.4s ease-out;
  flex: 1;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 24px 20px 80px;
  }
`;

const QuestionText = styled.h2`
  color: var(--zyvgo-text-primary, #222222);
  font-size: 1.5rem;
  margin: 0 0 12px 0;
  font-weight: 700;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HelperText = styled.p`
  color: var(--zyvgo-text-secondary, #666666);
  font-size: 1rem;
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const ContinueButton = styled.button`
  background: var(--zyvgo-primary, #222222);
  color: #ffffff;
  border: none;
  padding: 16px 48px;
  border-radius: 30px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--zyvgo-font-family);
  transition: all 0.2s ease;
  min-width: 200px;
  display: block;
  margin: 32px auto 0;
  animation: ${popIn} 0.3s ease-out;

  &:hover:not(:disabled) {
    background: #000000;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;

  }
`;

const LoginLinkContainer = styled.div`
  margin-top: 16px;
  text-align: center;
  font-size: 0.9375rem;
  color: #666;
`;

const LoginLink = styled.button`
  background: none;
  border: none;
  color: var(--zyvgo-primary, #222222);
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  text-decoration: underline;
  font-size: inherit;

  &:hover {
    color: #000;
  }
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: var(--zyvgo-error, #e74c3c);
  padding: 14px 18px;
  border-radius: var(--zyvgo-radius-md, 8px);
  margin-bottom: 20px;
  border-left: 4px solid var(--zyvgo-error, #e74c3c);
  animation: ${fadeIn} 0.3s ease-out;
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const CompletionContainer = styled.div`
  text-align: center;
  padding: 48px 32px;
  max-width: 500px;
  margin: 60px auto 0;
  animation: ${fadeIn} 0.6s ease-out;
`;

const CompletionIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: linear-gradient(
    135deg,
    var(--zyvgo-success, #50b37a) 0%,
    #38a169 100%
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: white;
`;

const CompletionTitle = styled.h2`
  color: var(--zyvgo-text-primary, #222222);
  font-size: 1.75rem;
  margin: 0 0 12px 0;
  font-weight: 700;
`;

const CompletionMessage = styled.p`
  color: var(--zyvgo-text-secondary, #666666);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
`;

const DisqualificationContainer = styled.div`
  text-align: center;
  padding: 48px 32px;
  max-width: 500px;
  margin: 60px auto 0;
  background: #fff8f1;
  border: 2px solid #f4d2b1;
  border-radius: var(--zyvgo-radius-lg, 12px);
  animation: ${fadeIn} 0.6s ease-out;
`;

const DisqualificationIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: #fed7aa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
`;

const DisqualificationTitle = styled.h2`
  color: var(--zyvgo-text-primary, #222222);
  font-size: 1.5rem;
  margin: 0 0 12px 0;
  font-weight: 700;
`;

const DisqualificationText = styled.p`
  color: var(--zyvgo-text-secondary, #666666);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
`;

const NoProductsContainer = styled.div`
  margin: 24px 0;
  animation: ${fadeIn} 0.6s ease-out;
`;

const NoProductsMessage = styled.p`
  text-align: center;
  color: var(--zyvgo-text-secondary, #666666);
  font-size: 1rem;
  padding: 48px 24px;
  background: #fff8f1;
  border: 2px solid #f4d2b1;
  border-radius: var(--zyvgo-radius-lg, 12px);
  line-height: 1.6;
  margin: 0;
`;

// ===== MAIN COMPONENT =====
const QuestionnaireForm = ({
  templateId,
  patientId,
  clientConfig,
  visitConfig,
  visitType,
  onComplete,
  couponCode,
  prefillData,
  isFollowUp,
  followUpAccessToken,
  previewProgramId,
  previewApiBaseUrl,
  runtimeProgramId,
  runtimeCustomProgramId,
  previewCustomProgramId,
  previewCapabilityToken,
  previewSnapshotChecksum,
  previewSnapshotId,
  runtimeApiBaseUrl,
}) => {
  const launchUrlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const urlProgramId = launchUrlParams.get("program_id") || null;
  const urlCustomProgramId = launchUrlParams.get("custom_program_id") || null;
  // Older visit-config responses can omit the runtime block even though the
  // App resolved the immutable Program launch from the URL. Keep the runtime
  // source available to the hook from either contract so we never fall back
  // to an empty legacy questionnaire shell.
  const effectiveRuntimeProgramId = runtimeProgramId || (
    visitConfig?.runtime?.type === "program"
      ? visitConfig.runtime.id
      : urlProgramId
  );
  const effectiveRuntimeCustomProgramId = runtimeCustomProgramId || (
    visitConfig?.runtime?.type === "custom_program"
      ? visitConfig.runtime.id
      : urlCustomProgramId
  );
  const journeyBoundary = useMemo(
    () => createJourneyBoundary({ previewCapabilityToken }),
    [previewCapabilityToken],
  );
  const isPreviewMode = journeyBoundary.isPreview;
  const {
    identity: previewIdentity,
    requestId: previewIdentityRequestId,
    receivedAt: previewIdentityReceivedAt,
  } = usePreviewIdentity();
  const isTreatmentJourney = Boolean(
    effectiveRuntimeProgramId ||
    effectiveRuntimeCustomProgramId ||
    previewProgramId ||
    previewCustomProgramId ||
    previewCapabilityToken
  );
  const isCustomProgramJourney = Boolean(
    effectiveRuntimeCustomProgramId || previewCustomProgramId,
  );
  const previousPreviewIdentityRef = useRef(previewIdentity);
  const previewIdentityResetSequenceRef = useRef(0);
  const [previewDiscountRate, setPreviewDiscountRate] = useState(0);
  const handlePreviewDiscountRateChange = useCallback((rate) => {
    setPreviewDiscountRate(isPreviewMode ? rate : 0);
  }, [isPreviewMode]);
  useEffect(() => {
    if (!isPreviewMode) {
      setPreviewDiscountRate(0);
    }
  }, [isPreviewMode]);
  const previewPaymentAdapter = useMemo(() => isPreviewMode ? {
    ...journeyBoundary.paymentAdapter,
    renderForm: () => (
      <PreviewPaymentFields
        onDiscountRateChange={handlePreviewDiscountRateChange}
      />
    ),
  } : null, [handlePreviewDiscountRateChange, isPreviewMode, journeyBoundary]);
  const FLOW_STATES = {
    // 🚫 DISABLED: Welcome and patient info now handled by backend questions
    // WELCOME: "welcome",
    // PATIENT_INFO: "patient_info",
    QUESTIONNAIRE: "questionnaire",
    PAYMENT: "payment",
    COMPLETED: "completed",
    DISQUALIFIED: "disqualified",
  };

  // 🚫 DISABLED: Start directly with questionnaire (patient info is now part of questions)
  const [currentFlow, setCurrentFlow] = useState(FLOW_STATES.QUESTIONNAIRE);
  const [patientInfo, setPatientInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [completionResult, setCompletionResult] = useState(null);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [addressSuggestionPrompt, setAddressSuggestionPrompt] = useState(null);
  const [loginInitialEmail, setLoginInitialEmail] = useState("");
  const [bmiError, setBMIError] = useState(null);
  const [dobError, setDobError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [careLocationAvailability, setCareLocationAvailability] = useState(null);
  const [patientIdentityRevision, setPatientIdentityRevision] = useState(0);
  const [prefillSourcesForVisibility, setPrefillSourcesForVisibility] = useState(
    prefillData?.prefill_sources || {}
  );
  const paymentRef = useRef(null);
  const patientProfileFetchAttemptRef = useRef(null);
  const checkoutRedirectTokenRef = useRef(null);
  const groupedQuestionRef = useRef(null);
  const addressSuggestionResolverRef = useRef(null);
  const reviewedAddressSuggestionsRef = useRef(new Set());
  const hasProductsRef = useRef(true);
  const careLocationInFlightRef = useRef(false);
  const legacyAutoAdvanceTimerRef = useRef(null);
  const treatmentDisqualificationIndexRef = useRef(null);
  const authStateRef = useRef(null);
  const intakeAuthenticationAdvancedRef = useRef(false);
  const previousJourneyStepIdRef = useRef("");
  const overallProgressHistoryRef = useRef(
    createOverallQuestionProgressHistory(),
  );
  useEffect(() => () => {
    if (legacyAutoAdvanceTimerRef.current) {
      clearTimeout(legacyAutoAdvanceTimerRef.current);
    }
  }, []);
  const initialProgramAuth = useMemo(
    () => (
      isTreatmentJourney && !isPreviewMode
        ? loadProgramAuthentication()
        : null
    ),
    [isPreviewMode, isTreatmentJourney],
  );
  const {
    questionnaire,
    currentQuestion,
    responses,
    updateResponse,
    wasLastResponseDisqualified,
    batchUpdateResponses,
    getResponsesSnapshot,
    nextQuestion,
    navigateToTreatmentAuthorityState,
    navigateToCurrentTreatmentAuthority,
    navigateToStableQuestion,
    previousQuestion,
    complete,
    validateResponse,
    getQuestionValidationError,
    disqualified,
    disqualificationReason,
    error: questionnaireError,
    treatmentRuntimeError,
    earlierIntakeChoice,
    startRequestedTreatment,
    continueEarlierTreatment,
    loading: questionnaireLoading,
    isRehydrating,
    visibleQuestions,
    currentQuestionIndex,
    completed,
    patientInfo: questionnairePatientInfo,
    setPatientInfo: setQuestionnairePatientInfo,
    clearDisqualification,
    triggerDisqualification,
    setCurrentQuestionIndex,
    questionnaireBranding: tenantQuestionnaireBranding,
    treatmentAuthority,
    treatmentCheckout,
    treatmentCheckoutRecovery,
    treatmentRecoveryMessage,
    treatmentQuoteChange,
    confirmTreatmentQuoteChange,
    recoverTreatmentCheckoutAction,
    synchronizeTreatmentAuthority,
    continueTreatmentWithEligiblePrograms,
    validateTreatmentLocation,
    checkpointTreatmentProgramAnswer,
    bindTreatmentPatientAuthority,
    updateTreatmentPreviewProfile,
    resetTreatmentPreviewAuthority,
    resetTreatmentPatientAuthority,
    resetTreatmentPatientState,
    rewindTreatmentResponses,
    removeQuestionnaireFile,
    uploadTreatmentRuntimeFile,
    retryInitialization: retryQuestionnaireInitialization,
  } = useFrontendQuestionnaire(templateId, patientId, {
    prefillSources: prefillSourcesForVisibility,
    disableLocalDraft: isFollowUp,
    previewProgramId,
    previewApiBaseUrl,
    runtimeProgramId: effectiveRuntimeProgramId,
    runtimeCustomProgramId: effectiveRuntimeCustomProgramId,
    previewCustomProgramId,
    previewCapabilityToken,
    previewSnapshotChecksum,
    previewSnapshotId,
    runtimeApiBaseUrl,
  });
  const prefilledQuestions = useRef(new Set());
  const followUpPrefillRefreshAttempted = useRef(false);

  const clearProgramPatientUiState = useCallback(() => {
    intakeAuthenticationAdvancedRef.current = false;
    setPatientInfo(null);
    setQuestionnairePatientInfo?.(null);
    setValidationError(null);
    setError(null);
    setBMIError(null);
    setDobError(null);
    setPaymentError(null);
    setPrefillSourcesForVisibility({});
    prefilledQuestions.current.clear();
    clearDisqualification();
    setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
    setPatientIdentityRevision((revision) => revision + 1);
  }, [
    FLOW_STATES.QUESTIONNAIRE,
    clearDisqualification,
    setQuestionnairePatientInfo,
  ]);

  const resetProgramPatientIdentity = useCallback(async () => {
    if (!isTreatmentJourney || isPreviewMode) return;
    clearProgramAuthentication();
    const clearedAuthState = {
      authenticated: false,
      sessionId: null,
      accessToken: null,
      patient: null,
      prefillData: null,
    };
    authStateRef.current = clearedAuthState;
    setAuthState(clearedAuthState);
    clearProgramPatientUiState();
    await resetTreatmentPatientState();
  }, [
    clearProgramPatientUiState,
    isPreviewMode,
    isTreatmentJourney,
    resetTreatmentPatientState,
  ]);

  const qcBranding = useMemo(() => {
    return resolveQuestionnaireBranding({
      baseBranding: clientConfig?.questionnaire_config,
      runtimeBranding: tenantQuestionnaireBranding,
      // Program and Custom Program pages intentionally use WellieMD's shared
      // black theme. The client logo remains available in the header.
      useWellieMdTheme: isTreatmentJourney,
    });
  }, [
    clientConfig?.questionnaire_config,
    isTreatmentJourney,
    tenantQuestionnaireBranding,
  ]);

  useEffect(() => {
    if (!isTreatmentJourney || treatmentCheckout?.state !== "checkout_ready") {
      return;
    }
    if (isPreviewMode || treatmentCheckout?.mode === "preview") {
      setCurrentFlow(FLOW_STATES.PAYMENT);
      return;
    }
    if (!treatmentCheckout.checkout_capability) {
      return;
    }
    const redirectToken = String(
      treatmentCheckout.session_id
      || treatmentCheckout.runtime_session_id
      || treatmentCheckout.checkout_capability,
    );
    if (checkoutRedirectTokenRef.current === redirectToken) {
      return;
    }
    checkoutRedirectTokenRef.current = redirectToken;
    redirectToTreatmentCheckout(treatmentCheckout, patientInfo, qcBranding, clientConfig?.id);
  }, [
    FLOW_STATES.PAYMENT,
    clientConfig?.id,
    isPreviewMode,
    isTreatmentJourney,
    patientInfo,
    qcBranding,
    treatmentCheckout,
  ]);

  const rawHeaderLogo = pickQuestionnaireLogoUrl(qcBranding);
  let headerLogoUrl = rawHeaderLogo;
  if (headerLogoUrl && headerLogoUrl.includes("localstack")) {
    headerLogoUrl = headerLogoUrl.replace("localstack", "localhost");
  }
  const [headerLogoFailed, setHeaderLogoFailed] = useState(false);
  useEffect(() => {
    setHeaderLogoFailed(false);
  }, [rawHeaderLogo]);

  // Sync rehydrated patient identity from hook into form state so completion has it after refresh
  useEffect(() => {
    if (questionnairePatientInfo != null && patientInfo == null) {
      setPatientInfo(questionnairePatientInfo);
    }
  }, [questionnairePatientInfo, patientInfo]);

  // Store responses globally for BMI calculation
  useEffect(() => {
    if (responses) {
      window.questionnaireResponses = responses;
    }
  }, [responses]);

  // Calculate progress percentage
  const progress =
    visibleQuestions && visibleQuestions.length > 0
      ? ((currentQuestionIndex + 1) / visibleQuestions.length) * 100
      : 0;

  const getCompanyName = useCallback(() => {
    return (
      qcBranding?.company_name ||
      clientConfig?.name ||
      ""
    );
  }, [clientConfig, qcBranding]);

  const effectiveClientConfig = useMemo(() => ({
    ...(clientConfig || {}),
    name: qcBranding?.company_name || clientConfig?.name,
    questionnaire_config: qcBranding || clientConfig?.questionnaire_config,
  }), [clientConfig, qcBranding]);

  // Preview receives the tenant branding from the immutable capability
  // response after the iframe has initialized. Apply it here as well as in
  // App's normal client-config path so preview and live questionnaire styles
  // stay identical without persisting preview state or performing API calls.
  useEffect(() => {
    // Program journeys must also apply an empty config: that explicitly
    // restores the WellieMD black defaults after any prior client branding.
    if (
      (!qcBranding || Object.keys(qcBranding).length === 0)
      && !isTreatmentJourney
    ) return;
    clientConfigService.applyBranding({
      ...effectiveClientConfig,
      branding_config: qcBranding || {},
    });
  }, [effectiveClientConfig, isTreatmentJourney, qcBranding]);
  const treatmentOrdersUrl = useMemo(
    () => buildTreatmentOrdersUrl(clientConfig?.patient_portal_domain),
    [clientConfig?.patient_portal_domain],
  );

  const calculateAge = useCallback((dateOfBirth) => {
    if (!dateOfBirth) return null;

    try {
      let birthDate;
      if (dateOfBirth.includes("/")) {
        const [month, day, year] = dateOfBirth.split("/");
        birthDate = new Date(year, month - 1, day);
      } else if (dateOfBirth.includes("-")) {
        birthDate = new Date(dateOfBirth);
      } else {
        return null;
      }

      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age;
    } catch (error) {
      console.error("Error calculating age:", error);
      console.error("Error calculating age:", error);
      return null;
    }
  }, []);

  const isFirstQuestion = useCallback(() => {
    return currentQuestionIndex === 0;
  }, [currentQuestionIndex]);

  const isLastQuestion = useCallback(() => {
    return (
      visibleQuestions && currentQuestionIndex === visibleQuestions.length - 1
    );
  }, [currentQuestionIndex, visibleQuestions]);

  const activeProductSelectionGroup = useMemo(() => {
    if (
      !isTreatmentJourney
      || !currentQuestion
      || currentQuestion.question_type !== "product_selection"
      || !Array.isArray(visibleQuestions)
      || !visibleQuestions.length
    ) {
      return null;
    }

    const bounds = productSelectionGroupBounds(
      visibleQuestions,
      currentQuestionIndex,
      { customProgram: isCustomProgramJourney },
    );
    if (!bounds) return null;
    const { startIndex, endIndex } = bounds;

    const questions = visibleQuestions.slice(startIndex, endIndex + 1);
    if (!questions.length) {
      return null;
    }

    const groupedChoices = [];
    const groupedChoiceKeys = new Set();
    const selectedChoices = [];

    questions.forEach((question) => {
      const choices = Array.isArray(question.answer_choices)
        ? question.answer_choices
        : (Array.isArray(question.checkout_config?.products) ? question.checkout_config.products : []);

      choices.forEach((choice) => {
        const groupedChoice = {
          ...(typeof choice === "object" && choice !== null ? choice : { value: choice, label: String(choice) }),
          __sourceQuestionId: question.id,
          __sourceQuestionText: question.question_text,
        };
        const choiceKey = getProductSelectionChoiceKey(groupedChoice);
        if (choiceKey && groupedChoiceKeys.has(choiceKey)) {
          return;
        }
        if (choiceKey) {
          groupedChoiceKeys.add(choiceKey);
        }
        groupedChoices.push(groupedChoice);
      });

      const response = responses[question.id];
      if (Array.isArray(response)) {
        response.forEach((item) => {
          if (item !== null && item !== undefined && item !== "") {
            selectedChoices.push(item);
          }
        });
      } else if (response !== null && response !== undefined && response !== "") {
        selectedChoices.push(response);
      }
    });

    const groupedContract = groupedProductSelectionContract(questions);
    const syntheticQuestion = {
      ...questions[0],
      ...groupedContract,
      id: `grouped-product-selection-${questions.map((q) => q.id).join("-")}`,
      question_text: questions.length > 1 ? "Product Options" : questions[0].question_text,
      helper_text: questions.length > 1 ? "Choose one or more products for this follow-up." : questions[0].helper_text,
      answer_choices: groupedChoices,
      validation: {
        ...(questions[0].validation || {}),
        ...groupedContract.validation,
      },
    };

    return {
      startIndex,
      endIndex,
      questions,
      syntheticQuestion,
      selectedChoices,
    };
  }, [
    currentQuestion,
    currentQuestionIndex,
    isCustomProgramJourney,
    isTreatmentJourney,
    responses,
    visibleQuestions,
  ]);

  const currentDisplayQuestion = activeProductSelectionGroup?.syntheticQuestion || currentQuestion;
  const currentDisplayResponse = activeProductSelectionGroup?.selectedChoices || (currentQuestion ? responses[currentQuestion.id] : undefined);
  const careLocationRejected = Boolean(
    isTreatmentJourney
    && currentDisplayQuestion?.question_type === "state_routing"
    && careLocationAvailability?.available === false
    && careLocationAvailability?.state === normalizeStateCode(currentDisplayResponse)
  );

  // Validation messages belong to the question that produced them. Boundary
  // synchronization can redirect to a missing question and set a generic
  // message before React renders that new question; clear that transient
  // message once the displayed question actually changes.
  // One sibling treatment cannot be sold and the rest still can; this holds
  // the offer to continue without it. Declared before the effect that clears
  // it so the binding is never referenced ahead of its initializer.
  const [blockedTreatment, setBlockedTreatment] = useState(null);

  useEffect(() => {
    setValidationError(null);
    // The offer belongs to the error that produced it. If it outlived that
    // error it would attach a stale "Continue without X" button to whatever
    // unrelated message came next.
    setBlockedTreatment(null);
    setCareLocationAvailability(null);
  }, [currentDisplayQuestion?.id]);

  const journeySteps = useMemo(
    () => isTreatmentJourney
      ? journeyBoundary.projectJourney(visibleQuestions, getCompanyName())
      : [],
    [getCompanyName, isTreatmentJourney, journeyBoundary, visibleQuestions],
  );
  const currentJourneyStep = useMemo(
    () => activeJourneyStep(journeySteps, currentQuestionIndex),
    [currentQuestionIndex, journeySteps],
  );
  const previousTreatmentStep = useMemo(() => {
    if (!isTreatmentJourney || !currentJourneyStep) return null;
    return previousRuntimeJourneyStep(journeySteps, currentJourneyStep.id);
  }, [
    currentJourneyStep,
    isTreatmentJourney,
    journeySteps,
  ]);
  const currentLabStage = responses?.[currentDisplayQuestion?.id]?.lab_stage || "collection";
  const hasPreviousTreatmentPhase = useMemo(() => {
    if (
      !isTreatmentJourney
      || previousTreatmentStep
    ) return false;
    if (
      currentDisplayQuestion?.question_type === "program_lab_checkout"
      && currentLabStage !== "collection"
    ) return false;
    return Boolean(previousVisibleAuthorityState({
      visibleQuestions,
      currentQuestion: currentDisplayQuestion,
      stateForQuestion: authorityStateForQuestion,
    }));
  }, [
    currentDisplayQuestion,
    currentLabStage,
    isTreatmentJourney,
    previousTreatmentStep,
    visibleQuestions,
  ]);
  const hasPreviousLabStage = (
    currentDisplayQuestion?.question_type === "program_lab_checkout"
    && ["aoe", "consents"].includes(
      responses?.[currentDisplayQuestion.id]?.lab_stage,
    )
  );
  const authoritativeCanGoBack = (
    treatmentAuthority?.presentation_contract_version === "1"
    && typeof treatmentAuthority?.can_go_back === "boolean"
  ) ? treatmentAuthority.can_go_back : null;
  const canGoBackWithinQuestionnaire = isTreatmentJourney
    ? authoritativeCanGoBack ?? (hasPreviousLabStage
      || hasPreviousTreatmentPhase
      || Boolean(previousTreatmentStep)
      || (!currentJourneyStep && currentQuestionIndex > 0))
    : !isFirstQuestion();
  useEffect(() => {
    trackAuthenticationStepVisit({
      advanceRef: intakeAuthenticationAdvancedRef,
      previousStepIdRef: previousJourneyStepIdRef,
      stepId: currentJourneyStep?.id,
      isAuthentication: (
        currentJourneyStep?.question?.question_type === "patient_authentication"
      ),
    });
  }, [
    currentJourneyStep?.id,
    currentJourneyStep?.question?.question_type,
  ]);
  const checkoutJourneyStep = useMemo(
    () => buildTerminalJourneyStep(
      journeySteps,
      TREATMENT_TERMINAL_STEP.checkout,
    ),
    [journeySteps],
  );
  const treatmentOrderSummary = useMemo(() => {
    return resolveTreatmentOrderSummary(treatmentCheckout);
  }, [treatmentCheckout]);
  const checkoutReturnQuestionIndex = useMemo(() => {
    for (let index = visibleQuestions.length - 1; index >= 0; index -= 1) {
      if (
        ["product_selection", "program_lab_checkout"].includes(
          visibleQuestions[index]?.question_type,
        )
      ) {
        return index;
      }
    }
    return checkoutJourneyStep.previousIndex;
  }, [checkoutJourneyStep.previousIndex, visibleQuestions]);
  const allJourneySteps = useMemo(
    () => (checkoutJourneyStep ? [...journeySteps, checkoutJourneyStep] : journeySteps),
    [journeySteps, checkoutJourneyStep],
  );
  const journeyMachine = useJourneyStateMachine(
    allJourneySteps,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    isCustomProgramJourney,
  );
  const runtimePresentation = useMemo(
    () => isTreatmentJourney
      ? presentationForRuntimeQuestion({
          presentation: treatmentAuthority,
          question: currentDisplayQuestion,
        })
      : null,
    [currentDisplayQuestion, isTreatmentJourney, treatmentAuthority],
  );
  const runtimeJourneyProgress = runtimePresentation?.journey_presentation?.progress;
  const possibleOverallTotal = useMemo(
    () => runtimePresentation?.journey_presentation?.kind === "program"
      ? possibleTreatmentQuestionCount({
          questionnaire,
          responses,
          authorityState: treatmentAuthority?.state,
        })
      : null,
    [questionnaire, responses, runtimePresentation, treatmentAuthority?.state],
  );
  const terminalProductQuestion = (
    treatmentAuthority?.state === TREATMENT_RUNTIME_STATE.productSelection
    && currentQuestion?.question_type === "product_selection"
    && (activeProductSelectionGroup?.endIndex ?? currentQuestionIndex)
      === visibleQuestions.length - 1
  );
  const overallProgressReleaseKey = (
    runtimePresentation?.presentation?.owner?.release_id
    || `${questionnaire?.id || "questionnaire"}:${questionnaire?.version || ""}`
  );
  const overallProgressBranchKey = useMemo(
    () => visibleQuestions
      .map((question) => questionProgressIdentity(question))
      .join("|"),
    [visibleQuestions],
  );
  const overallJourneyProgress = useMemo(
    () => {
      if (!isTreatmentJourney) return null;
      const result = updateOverallQuestionProgress({
        history: overallProgressHistoryRef.current,
        releaseKey: overallProgressReleaseKey,
        allQuestions: questionnaire?.questions,
        currentQuestion,
        runtimeProgress: runtimeJourneyProgress,
        possibleTotal: possibleOverallTotal,
        branchKey: overallProgressBranchKey,
        completed: completed || currentFlow === FLOW_STATES.PAYMENT,
        terminalQuestion: terminalProductQuestion,
      });
      overallProgressHistoryRef.current = result.history;
      return result.progress;
    },
    [
      completed,
      currentQuestion,
      currentFlow,
      FLOW_STATES.PAYMENT,
      isTreatmentJourney,
      questionnaire?.questions,
      overallProgressBranchKey,
      overallProgressReleaseKey,
      possibleOverallTotal,
      runtimeJourneyProgress,
      terminalProductQuestion,
    ],
  );
  const runtimeProgressState = runtimeProgressBarState(
    overallJourneyProgress || runtimeJourneyProgress,
  );
  const displayedProgress = isTreatmentJourney
    ? runtimeProgressState.percent
    : progress;
  useJourneyScrollRestoration(
    isTreatmentJourney,
    currentFlow === FLOW_STATES.PAYMENT
      ? checkoutJourneyStep.id
      : currentJourneyStep?.id,
  );

  useEffect(() => {
    if (
      !isTreatmentJourney
      || currentFlow !== FLOW_STATES.PAYMENT
      || treatmentOrderSummary
    ) {
      return;
    }
    if (Number.isInteger(checkoutReturnQuestionIndex)) {
      setCurrentQuestionIndex(checkoutReturnQuestionIndex);
    }
    setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
    setValidationError(
      "Checkout was not prepared. Review your treatment selection and try again.",
    );
  }, [
    FLOW_STATES.PAYMENT,
    FLOW_STATES.QUESTIONNAIRE,
    checkoutReturnQuestionIndex,
    currentFlow,
    isTreatmentJourney,
    setCurrentQuestionIndex,
    treatmentOrderSummary,
  ]);

  const resetTreatmentPreviewAuthorityRef = useRef(resetTreatmentPreviewAuthority);
  useEffect(() => {
    resetTreatmentPreviewAuthorityRef.current = resetTreatmentPreviewAuthority;
  }, [resetTreatmentPreviewAuthority]);

  useEffect(() => {
    const previousIdentity = previousPreviewIdentityRef.current;
    previousPreviewIdentityRef.current = previewIdentity;
    if (!isPreviewMode || previousIdentity === previewIdentity) return;
    const resetSequence = ++previewIdentityResetSequenceRef.current;
    const requestId = previewIdentityRequestId || null;
    const resetStartedAt = performance.now();
    let cancelled = false;
    let acknowledged = false;
    const acknowledge = (message = "") => {
      if (acknowledged) return;
      acknowledged = true;
      window.dispatchEvent(
        new CustomEvent(PREVIEW_PROTOCOL.events.identityReady, {
          detail: {
            identity: previewIdentity,
            requestId,
            message,
            resetDurationMs: Math.round(performance.now() - resetStartedAt),
            requestReceivedAt: previewIdentityReceivedAt,
          },
        }),
      );
    };

    setPatientInfo(null);
    setQuestionnairePatientInfo?.(null);
    setValidationError(null);
    setLoginInitialEmail("");
    setShowLoginModal(false);
    setAuthState((previous) => ({
      ...previous,
      authenticated: false,
      sessionId: null,
      accessToken: null,
      patient: null,
      prefillData: null,
    }));
    setPrefillSourcesForVisibility({});
    prefilledQuestions.current.clear();

    const resetIdentity = async () => {
      let resetMessage = "";
      try {
        await resetTreatmentPreviewAuthorityRef.current();
      } catch (resetError) {
        resetMessage = resetError.message || "Preview identity could not be reset.";
      }
      if (
        cancelled
        || resetSequence !== previewIdentityResetSequenceRef.current
      ) {
        return;
      }

      if (!resetMessage) {
        setCurrentQuestionIndex(0);
      }
      acknowledge(resetMessage);
    };
    resetIdentity();
    return () => {
      cancelled = true;
    };
  }, [
    isPreviewMode,
    previewIdentity,
    previewIdentityReceivedAt,
    previewIdentityRequestId,
    setQuestionnairePatientInfo,
  ]);

  // Clear dobError whenever we navigate away from the DOB question step.
  // This prevents the error from bleeding into unrelated steps (e.g. Personal Details).
  useEffect(() => {
    const isDobQuestion =
      currentQuestion?.question_type === 'date' &&
      (
        currentQuestion?.beluga_field_mapping === 'date_of_birth' ||
        currentQuestion?.beluga_field_mapping === 'dob' ||
        (currentQuestion?.question_text || '').toLowerCase().includes('date of birth')
      );
    if (!isDobQuestion) {
      setDobError(null);
    }
  }, [currentQuestion]);

  // Check if Continue button should be shown
  useEffect(() => {
    if (!currentDisplayQuestion) {
      setShowContinueButton(false);
      return;
    }

    // A BMI calculation error blocks advancement until its inputs change.
    // Generic validation errors remain retryable, so they must not remove the
    // patient's Continue action. DOB errors disable that action in JSX.
    if (bmiError) {
      setShowContinueButton(false);
      return;
    }

    const questionType = currentDisplayQuestion.question_type;
    // Required product options are rendered as "Included automatically" by
    // TreatmentProductSelection.  They are normally persisted by the
    // component's default-selection effect, but Continue can be clicked in
    // the same render before that effect has committed.  Materialize the
    // authoritative selections here so the runtime transition never receives
    // an empty product-selection payload.
    let currentResponse = currentDisplayResponse;
    if (activeProductSelectionGroup) {
      const selectedItems = Array.isArray(currentResponse)
        ? [...currentResponse]
        : (currentResponse ? [currentResponse] : []);
      currentResponse = materializeRequiredProductSelections(
        activeProductSelectionGroup.syntheticQuestion.answer_choices,
        selectedItems,
      );
    }

    // Show button for text inputs, textareas, and number inputs when there's content
    if (
      questionType === "text" ||
      questionType === "textarea" ||
      questionType === "number" ||
      questionType === "medical_conditions" ||
      questionType === "self_reported_meds" ||
      questionType === "allergies"
    ) {
      setShowContinueButton(
        !!currentResponse && String(currentResponse).trim() !== ""
      );
    }
    // Show Continue for medication dose selector when a combined answer is selected
    else if (questionType === "medication_dose_selector") {
      setShowContinueButton(
        !!currentResponse && String(currentResponse).trim() !== ""
      );
    }
    // Always show for file uploads, height/weight, bmi, multiple choice
    else if (
      questionType === "file_upload" ||
      questionType === "height_weight" ||
      questionType === "bmi" ||
      questionType === "multiple_choice" ||
      questionType === "consent"
    ) {
      setShowContinueButton(true);
    }
    // Treatment journeys synchronize every boundary with server authority,
    // so their choice questions use an explicit Continue action.
    else if (questionType === "single_choice" || questionType === "sex" || questionType === "labs") {
      setShowContinueButton(isTreatmentJourney);
    }
    else if (questionType === "product_selection") {
      setShowContinueButton(true);
    }
    // Checkout questions always show Continue button (response is auto-stored)
    else if (questionType === "checkout") {
      setShowContinueButton(true);
    } else {
      setShowContinueButton(true);
    }
  }, [
    currentDisplayQuestion,
    currentDisplayResponse,
    bmiError,
    isTreatmentJourney,
  ]);

  const synchronizeNextJourneyStep = useCallback(async (
    patientInfoOverride = null,
    responsesOverride = null,
  ) => {
    if (!isTreatmentJourney || !currentJourneyStep) return null;
    // The screening intro is a client-side orientation page. It deliberately
    // has no released Program-answer node, so Continue must advance locally
    // instead of submitting a checkpoint with its synthetic source ID.
    if (
      currentJourneyStep.question?.question_type
      === "treatment_screening_intro"
    ) return null;
    // The potential-match selector is a client-side projection boundary. Its
    // positional next step can be another routing item, but continuing here
    // must first submit the selected Programs through select_treatments.
    // Otherwise the runtime remains in routing with selected_programs empty
    // and the preview/live journey can loop back to its opening step.
    if (
      currentJourneyStep.question?.question_type === "matched_program_selection"
      && currentJourneyStep.question?.matching_config?.stage !== "recommendations"
    ) {
      return synchronizeTreatmentAuthority(
        TREATMENT_RUNTIME_STATE.treatmentScreening,
        responsesOverride,
        patientInfoOverride,
      );
    }
    // Product questions may be conditionally hidden until the server accepts
    // the care location. In that pre-authority projection, state routing is
    // the last visible step and has no nextTargetId. Synchronize directly to
    // product selection so the authoritative response can reveal the options.
    if (
      !currentJourneyStep.nextTargetId
      && currentJourneyStep.question?.question_type === "state_routing"
    ) {
      return synchronizeTreatmentAuthority(
        TREATMENT_RUNTIME_STATE.productSelection,
        responsesOverride,
        patientInfoOverride,
      );
    }
    if (!currentJourneyStep.nextTargetId) return null;
    const nextStep = journeySteps.find(
      (step) => step.id === currentJourneyStep.nextTargetId,
    );
    if (!nextStep) return null;
    const transition = planStepTransition(
      journeySteps,
      currentJourneyStep.id,
      nextStep.id,
    );
    if (transition.mode === "local") {
      // Each library consent is a local page within the consent boundary.
      // Submit the complete set only when leaving that boundary.
      if (
        authorityStateForQuestion(currentJourneyStep.question)
        === TREATMENT_RUNTIME_STATE.consents
      ) return null;
      // `updateResponse` writes its ref synchronously, while React may not
      // have committed the matching `responses` render before the patient
      // presses Continue.  A local Program checkpoint must use that current
      // snapshot or it can post an empty answer and make the first Continue
      // click fail with `required_answers_missing`.
      const activeResponses = responsesOverride || getResponsesSnapshot();
      // Stage 1 Custom Program questions are not Program checkpoints. The
      // authority receives them as common-flow answers at the next boundary;
      // calling the Program checkpoint here produces a payload-less request
      // because these questions intentionally have no program_id.
      if (
        isCustomProgramJourney
        && !currentJourneyStep.question?.program_id
      ) return null;
      const checkpointQuestion = currentJourneyStep.question;
      const responseKey = [
        checkpointQuestion?.id,
        checkpointQuestion?.source_id,
        checkpointQuestion?.release_node_id,
      ].find((key) => (
        key !== undefined
        && key !== null
        && Object.prototype.hasOwnProperty.call(activeResponses || {}, key)
      ));
      return checkpointTreatmentProgramAnswer(
        checkpointQuestion,
        responseKey === undefined ? undefined : activeResponses[responseKey],
        activeResponses,
      );
    }
    return synchronizeTreatmentAuthority(
      authorityStateForQuestion(nextStep.question),
      responsesOverride,
      patientInfoOverride,
    );
  }, [
    currentJourneyStep?.id,
    currentJourneyStep?.nextTargetId,
    currentJourneyStep?.question?.question_type,
    isTreatmentJourney,
    isCustomProgramJourney,
    journeySteps,
    getResponsesSnapshot,
    checkpointTreatmentProgramAnswer,
    synchronizeTreatmentAuthority,
  ]);

  const handlePatientInfoSubmit = useCallback(
    async (formData) => {
      try {
        setLoading(true);
        setError(null);

        const age = calculateAge(formData.dob);
        const enhancedPatientInfo = {
          ...formData,
          age,
          name: `${formData.firstName} ${formData.lastName}`,
        };

        setPatientInfo(enhancedPatientInfo);

        if (setQuestionnairePatientInfo) {
          setQuestionnairePatientInfo(enhancedPatientInfo);
        }

        // Pre-populate mapped fields from patient info
        if (questionnaire?.questions) {
          // Map of beluga_field_mapping to patient info fields
          const fieldMappings = {};

          // Pre-populate each mapped field
          Object.entries(fieldMappings).forEach(([mapping, value]) => {
            if (value) {
              const question = questionnaire.questions.find(
                (q) => q.beluga_field_mapping === mapping
              );
              if (question) {
                updateResponse(question.id, value);
              }
            }
          });
        }

        if (questionnaire) {
          setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
        }
      } catch (err) {
        setError(getApiErrorMessage(err) || "Failed to process patient information");
      } finally {
        setLoading(false);
      }
    },
    [
      calculateAge,
      questionnaire,
      setQuestionnairePatientInfo,
      FLOW_STATES,
      updateResponse,
    ]
  );

  // Updated to actually complete the questionnaire and create order
  const handleQuestionnaireComplete = useCallback(async (responsesOverride = null) => {
    console.log(
      "🚀 handleQuestionnaireComplete called - starting completion..."
    );
    setLoading(true);
    setError(null);

    try {
      const result = await complete(patientInfo, null, responsesOverride);
      setCompletionResult(result);

      if (result.payment_required) {
        if (isTreatmentJourney && !isPreviewMode && result.checkout_ready) {
          redirectToTreatmentCheckout(
            result.checkout_ready,
            patientInfo,
            qcBranding,
            clientConfig?.id,
          );
          return;
        }
        setCurrentFlow(FLOW_STATES.PAYMENT);
        return;
      }

      // Checkout Refactor: If backend returns a checkout_url, redirect to it
      if (result.checkout_url) {
        console.log("🚀 Redirecting to checkout:", result.checkout_url);

        let bName = '';
        if (patientInfo?.name) {
          bName = patientInfo.name;
        } else if (patientInfo?.firstName || patientInfo?.lastName) {
          bName = `${patientInfo.firstName || ''} ${patientInfo.lastName || ''}`.trim();
        } else if (patientInfo?.first_name || patientInfo?.last_name) {
          bName = `${patientInfo.first_name || ''} ${patientInfo.last_name || ''}`.trim();
        }
        bName = bName || result.patient_name || '';
        const bEmail = patientInfo?.email || result.patient_email || '';

        if (bName) sessionStorage.setItem('welliemd_buyer_name', bName);
        if (bEmail) sessionStorage.setItem('welliemd_buyer_email', bEmail);

        window.location.href = result.checkout_url;
        return;
      }

      // If no checkout_url, show completion screen (legacy flow)
      setCurrentFlow(FLOW_STATES.COMPLETED);

      onComplete?.(result);

      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: "welliemd_questionnaire_completed",
            data: {
              ...result,
              patient_created: true,
            },
          },
          "*"
        );
      }
    } catch (error) {
      console.error("❌ Questionnaire completion failed:", error);
      setError(error.message || "Failed to complete questionnaire");
    } finally {
      setLoading(false);
    }
  }, [
    FLOW_STATES,
    clientConfig?.id,
    complete,
    isPreviewMode,
    isTreatmentJourney,
    onComplete,
    patientInfo,
    qcBranding,
  ]);

  // 🚫 PAYMENT DISABLED: Removed payment handling
  const handlePaymentAndSubmit = useCallback(async () => {
    setPaymentError(null);
    try {
      setLoading(true);
      if (
        !paymentRef.current ||
        typeof paymentRef.current.getPaymentData !== "function"
      ) {
        // No payment step available; proceed to complete normally
        await handlePaymentComplete(null);
        return;
      }

      const paymentPayload = await paymentRef.current.getPaymentData();

      await handlePaymentComplete(paymentPayload);
    } catch (err) {
      console.error("Payment submission error", err);
      setPaymentError(err.message || "Payment failed. Please try again.");
      setLoading(false);
    }
  }, [handleQuestionnaireComplete]);

  const handlePaymentComplete = useCallback(
    async (paymentData) => {
      setLoading(true);
      setError(null);

      try {
        const result = await complete(null, paymentData);
        setCompletionResult(result);

        if (result.payment_required) {
          if (isTreatmentJourney && !isPreviewMode && result.checkout_ready) {
            redirectToTreatmentCheckout(
              result.checkout_ready,
              patientInfo,
              qcBranding,
              clientConfig?.id,
            );
            return;
          }
          setCurrentFlow(FLOW_STATES.PAYMENT);
          return;
        }

        // Checkout Refactor: If backend returns a checkout_url, redirect to it
        if (result.checkout_url) {
          console.log("Redirecting to checkout:", result.checkout_url);

          let bName = '';
          if (patientInfo?.name) {
            bName = patientInfo.name;
          } else if (patientInfo?.firstName || patientInfo?.lastName) {
            bName = `${patientInfo.firstName || ''} ${patientInfo.lastName || ''}`.trim();
          } else if (patientInfo?.first_name || patientInfo?.last_name) {
            bName = `${patientInfo.first_name || ''} ${patientInfo.last_name || ''}`.trim();
          }

          bName = bName || result.patient_name || '';
          const bEmail = patientInfo?.email || result.patient_email || '';


          if (bName) sessionStorage.setItem('welliemd_buyer_name', bName);
          if (bEmail) sessionStorage.setItem('welliemd_buyer_email', bEmail);

          window.location.href = result.checkout_url;
          return;
        }

        setCurrentFlow(FLOW_STATES.COMPLETED);

        onComplete?.(result);

        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: "welliemd_questionnaire_completed",
              data: {
                ...result,
                patient_created: true,
              },
            },
            "*"
          );
        }
      } catch (error) {
        setError(getApiErrorMessage(error) || "Failed to complete questionnaire");
      } finally {
        setLoading(false);
      }
    },
    [
      FLOW_STATES,
      clientConfig?.id,
      complete,
      isPreviewMode,
      isTreatmentJourney,
      onComplete,
      patientInfo,
      qcBranding,
    ]
  );

  const handleResponseChange = useCallback(
    (questionId, responseValue) => {
      if (legacyAutoAdvanceTimerRef.current) {
        clearTimeout(legacyAutoAdvanceTimerRef.current);
        legacyAutoAdvanceTimerRef.current = null;
      }
      setValidationError(null);
      setError(null);
      const updatedResponses = updateResponse(questionId, responseValue);

      // Local disqualification also exists outside Custom Programs. A terminal
      // answer must never leave a delayed navigation callback behind.
      if (wasLastResponseDisqualified()) {
        return;
      }

      // Library consent refusals are server-authoritative. Submit an explicit
      // declined released option as soon as it is selected, rather than
      // waiting for Continue (and therefore every remaining consent). The
      // runtime returns the terminal projection, which renders the standard
      // Assessment Not Eligible screen without treating the other consents as
      // missing fields.
      if (
        isTreatmentJourney
        && treatmentAuthority?.state === TREATMENT_RUNTIME_STATE.consents
        && currentQuestion?.question_type === "consent"
        && responseValue?.agreed === false
        && responseValue?.selected_value !== undefined
        && responseValue?.selected_value !== null
        && responseValue?.selected_value !== ""
      ) {
        setLoading(true);
        synchronizeTreatmentAuthority(
          TREATMENT_RUNTIME_STATE.productSelection,
          updatedResponses,
        ).catch((authorityError) => {
          setValidationError(
            authorityError.message
              || "Unable to evaluate this consent choice.",
          );
        }).finally(() => {
          setLoading(false);
        });
        return;
      }

      // Auto-advance for single choice, sex, labs, and state_routing questions.
      // Product selection stays open so patients can pick multiple products.
      if (
        !isTreatmentJourney &&
        (currentQuestion?.question_type === "single_choice" ||
          currentQuestion?.question_type === "sex" ||
          currentQuestion?.question_type === "labs" ||
          currentQuestion?.question_type === "state_routing") &&
        responseValue
      ) {
        // Automatically proceed after a short delay
        legacyAutoAdvanceTimerRef.current = setTimeout(() => {
          legacyAutoAdvanceTimerRef.current = null;
          // Clear any validation errors
          setValidationError(null);

          // Always call nextQuestion - it will handle navigation properly
          // Don't check isLastQuestion here as it uses stale visibleQuestions state
          nextQuestion(updatedResponses, questionId);
        }, 300); // 300ms delay for better UX
      }
    },
    [
      updateResponse,
      wasLastResponseDisqualified,
      currentQuestion,
      isTreatmentJourney,
      nextQuestion,
      synchronizeTreatmentAuthority,
      treatmentAuthority?.state,
    ]
  );

  const requestAddressSuggestionChoice = useCallback((suggestion) => (
    new Promise((resolve) => {
      addressSuggestionResolverRef.current = resolve;
      setAddressSuggestionPrompt(suggestion);
    })
  ), []);

  const resolveAddressSuggestionChoice = useCallback((useSuggested) => {
    const resolve = addressSuggestionResolverRef.current;
    addressSuggestionResolverRef.current = null;
    setAddressSuggestionPrompt(null);
    resolve?.(useSuggested);
  }, []);

  useEffect(() => () => {
    addressSuggestionResolverRef.current?.(false);
    addressSuggestionResolverRef.current = null;
  }, []);

  const handleNext = useCallback(async () => {
    if (!currentDisplayQuestion) return;
    if (
      isTreatmentJourney &&
      !JOURNEY_SUPPORTED_QUESTION_TYPES.includes(
        currentDisplayQuestion.question_type,
      )
    ) {
      setValidationError(
        "This configured questionnaire item is unsupported and cannot be skipped.",
      );
      return;
    }

    // Check for BMI error - prevent navigation if BMI is too low
    if (bmiError) {
      setValidationError(
        "Please ensure you meet the BMI requirements to continue."
      );
      return;
    }

    // Check for DOB error - prevent navigation if DOB is invalid
    if (dobError) {
      if (dobError.toLowerCase().includes("eligible") || dobError.toLowerCase().includes("at least")) {
        triggerDisqualification(
          dobError,
          currentQuestion.question_text,
          currentQuestion.id,
          'age'
        );
      } else {
        setValidationError(dobError);
      }
      return;
    }

    const latestResponses = getResponsesSnapshot();
    let currentResponse = currentDisplayResponse;
    if (activeProductSelectionGroup) {
      // The displayed product page can represent several source questions.
      // Do not replace the merged response with only the first source row;
      // doing so makes later selected optional Products look unselected to
      // the cardinality validator.
      currentResponse = collectGroupedProductSelections(
        activeProductSelectionGroup.questions,
        latestResponses,
        currentDisplayResponse,
      );
    } else {
      const latestResponseKey = [
        currentDisplayQuestion.id,
        currentDisplayQuestion.source_id,
        currentDisplayQuestion.release_node_id,
      ].find((key) => (
        key && Object.prototype.hasOwnProperty.call(latestResponses, key)
      ));
      if (latestResponseKey) {
        currentResponse = latestResponses[latestResponseKey];
      }
    }
    let completionResponses = latestResponses;

    if (currentDisplayQuestion.question_type === "shipping_address") {
      const liveGroupedValues = groupedQuestionRef.current?.getValues?.();
      if (liveGroupedValues && typeof liveGroupedValues === "object") {
        currentResponse = liveGroupedValues;
      }
      const requiredFieldError = (
        groupedQuestionRef.current?.getRequiredFieldError?.()
      );
      if (requiredFieldError) {
        setValidationError(requiredFieldError);
        return;
      }
    }

    // Phase II care location is explicitly confirmed through the existing
    // runtime transition. Selecting a radio option only updates local state;
    // it must not advance or mutate the Patient profile.
    if (
      isTreatmentJourney
      && currentDisplayQuestion.question_type === "state_routing"
    ) {
      const state = normalizeStateCode(currentResponse);
      if (!state) {
        setValidationError(careLocationMessage("care_location_invalid"));
        return;
      }
      if (careLocationInFlightRef.current) return;
      careLocationInFlightRef.current = true;
      setLoading(true);
      setValidationError(null);
      const result = await selectCareLocation(state, {
        synchronize: async () => {
          // The Service Area Check has its own confirmation branch, so it
          // does not pass through the ordinary local-checkpoint path below.
          // Give its runtime transition the same explicit fresh snapshot: a
          // state selection and Continue can otherwise submit the prior
          // render and be rejected as a missing routing answer.
          const completionResponses = buildCompletionResponseSnapshot({
            responses,
            latestResponses,
            currentQuestion: currentDisplayQuestion,
            currentResponse: state,
          });
          const authority = await synchronizeNextJourneyStep(
            null,
            completionResponses,
          );
          if (
            authority?.state
            && navigateToTreatmentAuthorityState(authority.state)
          ) {
            return authority;
          }
          nextQuestion(undefined, currentDisplayQuestion.id);
          return authority;
        },
      });
      careLocationInFlightRef.current = false;
      setLoading(false);
      if (result.status === CARE_LOCATION_STATUS.advanced) {
        setCareLocationAvailability({ state, available: true });
        // Navigation is performed inside the synchronized callback so it is
        // based on the newly installed authority, not a stale positional
        // question index.
      } else {
        const message = careLocationMessage(
          result.error,
          result.details,
          result.message,
          {
            treatmentName: currentDisplayQuestion?.service_area_name,
            state,
          },
        );
        setCareLocationAvailability({
          state,
          available: false,
          message,
        });
        setValidationError(
          message,
        );
      }
      return;
    }

    // Required products are displayed as included automatically. Ensure
    // their release option IDs are present before validating or submitting
    // the grouped product-selection response.
    if (activeProductSelectionGroup) {
      currentResponse = materializeRequiredProductSelections(
        activeProductSelectionGroup.syntheticQuestion.answer_choices,
        Array.isArray(currentResponse)
        ? [...currentResponse]
        : (currentResponse ? [currentResponse] : []),
      );
    }

    if (currentDisplayQuestion.question_type === "product_selection") {
      const categoryError = productSelectionCategoryErrorAcrossQuestions({
        questions: visibleQuestions.filter((question) => (
          question.question_type === "product_selection"
        )),
        responses: latestResponses,
        currentQuestions: activeProductSelectionGroup?.questions
          || [currentDisplayQuestion],
        currentResponse,
      });
      if (categoryError) {
        setValidationError(categoryError);
        return;
      }
      const selectionError = productSelectionError(
        currentDisplayQuestion,
        currentResponse,
      );
      if (selectionError) {
        setValidationError(selectionError);
        return;
      }
    }

    const validationErrors = validateResponse(
      currentDisplayQuestion,
      currentResponse
    );

    if (validationErrors.length > 0) {
      setValidationError(validationErrors[0]);
      return;
    }

    // Custom Programs may contain several Programs, but checkout still creates
    // one treatment case per Treatment Type. Stop duplicate selections here so
    // the patient receives an actionable message before the runtime rejects
    // the same combination. Standalone Program journeys keep their existing
    // validation path.
    if (
      isCustomProgramJourney
      && currentDisplayQuestion.question_type === "matched_program_selection"
    ) {
      const selectionError = customProgramSelectionError(
        currentDisplayQuestion,
        currentResponse,
      );
      if (selectionError) {
        setValidationError(selectionError);
        return;
      }
    }

    if (activeProductSelectionGroup) {
      const groupedUpdates = {};
      const selectedItems = Array.isArray(currentResponse)
        ? currentResponse
        : (currentResponse ? [currentResponse] : []);

      activeProductSelectionGroup.questions.forEach((question) => {
        const allowedChoices = Array.isArray(question.answer_choices)
          ? question.answer_choices
          : (Array.isArray(question.checkout_config?.products) ? question.checkout_config.products : []);
        const allowedKeys = new Set(
          allowedChoices.map((choice) => getProductSelectionChoiceKey(choice))
        );

        const selectedForQuestion = selectedItems
          .filter((item) => {
            if (item && typeof item === "object" && item.__sourceQuestionId) {
              return String(item.__sourceQuestionId) === String(question.id);
            }
            return allowedKeys.has(getProductSelectionChoiceKey(item));
          })
          .map((item) => {
            if (item && typeof item === "object") {
              const sanitized = { ...item };
              if (!sanitized.choice_group && sanitized.__sourceQuestionId) {
                sanitized.choice_group = String(sanitized.__sourceQuestionId);
              }
              delete sanitized.__sourceQuestionId;
              delete sanitized.__sourceQuestionText;
              return sanitized;
            }
            return item;
          });

        groupedUpdates[question.id] = selectedForQuestion;
      });

      completionResponses = batchUpdateResponses(groupedUpdates)
        || { ...responses, ...groupedUpdates };
    }

    // Special handling for personal_details - trigger patient onboarding
    if (currentDisplayQuestion.question_type === "personal_details") {
      setLoading(true);
      let submittedEmail = "";
      try {
        // Create UUID to field name mapping from sub_questions
        const fieldMapping = {};
        const subQuestions = currentQuestion.sub_questions || [];

        // Create mapping from UUID (question.id) to field name
        subQuestions.forEach((subQ) => {
          // Map common field names
          if (
            subQ.question_text?.toLowerCase().includes("email") ||
            subQ.question_type === "email"
          ) {
            fieldMapping[subQ.id] = "email";
          } else if (
            subQ.question_text?.toLowerCase().includes("phone") ||
            subQ.question_type === "phone"
          ) {
            fieldMapping[subQ.id] = "phone";
          } else if (subQ.question_text?.toLowerCase().includes("first name")) {
            fieldMapping[subQ.id] = "first_name";
            fieldMapping[subQ.id + "_camel"] = "firstName"; // For robustness
          } else if (subQ.question_text?.toLowerCase().includes("last name")) {
            fieldMapping[subQ.id] = "last_name";
            fieldMapping[subQ.id + "_camel"] = "lastName"; // For robustness
          }
        });

        // Extract email, phone, first_name, last_name using the mapping
        let email = null;
        let phone = null;
        let firstName = null;
        let lastName = null;

        Object.entries(currentResponse || {}).forEach(([uuid, value]) => {
          if (fieldMapping[uuid] === "email") {
            email = value;
          } else if (fieldMapping[uuid] === "phone") {
            phone = value;
          } else if (fieldMapping[uuid] === "first_name") {
            firstName = value;
          } else if (fieldMapping[uuid] === "last_name") {
            lastName = value;
          }
        });
        submittedEmail = email || "";

        let currentAuthState = authStateRef.current;
        if (
          isTreatmentJourney
          && !isPreviewMode
          && currentAuthState?.authenticated
          && !patientIdentityMatchesEmail(currentAuthState.patient, email)
        ) {
          const changedEmail = email || "";
          currentAuthState = {
            authenticated: false,
            sessionId: null,
            accessToken: null,
            patient: null,
            prefillData: null,
          };
          authStateRef.current = currentAuthState;
          setAuthState(currentAuthState);
          await resetProgramPatientIdentity();
          setLoginInitialEmail(changedEmail);
        }
        const authenticatedPatient = currentAuthState?.authenticated
          && currentAuthState.patient;
        const resolvedPatient = authenticatedPatient
          ? {
              ...authenticatedPatient,
              patient_id: authenticatedPatient.patient_id || authenticatedPatient.id,
              email: authenticatedPatient.email || email,
              phone: authenticatedPatient.phone || phone,
              firstName: authenticatedPatient.first_name || firstName || undefined,
              lastName: authenticatedPatient.last_name || lastName || undefined,
              first_name: authenticatedPatient.first_name || firstName || undefined,
              last_name: authenticatedPatient.last_name || lastName || undefined,
              is_new_patient: false,
            }
          : await journeyBoundary.identityAdapter.resolve(
              {
                email,
                phone,
                firstName,
                lastName,
                ...(isPreviewMode ? previewPatientDefaults(questionnaire) : {}),
              },
              async () => {
                if (!email) {
                  throw new Error("Email is required to create your account.");
                }
                const onboardingResult = await api.onboardPatient(email, phone, {
                  first_name: firstName || undefined,
                  last_name: lastName || undefined,
                  template_id: templateId || undefined,
                  issue_patient_access_token: Boolean(
                    effectiveRuntimeProgramId || effectiveRuntimeCustomProgramId
                  ),
                });
                return {
                  patient_id: onboardingResult.patient_id,
                  is_new_patient: onboardingResult.is_new_patient,
                  email: onboardingResult.email || email,
                  phone: onboardingResult.phone || phone,
                  firstName: firstName || undefined,
                  lastName: lastName || undefined,
                  first_name: firstName || undefined,
                  last_name: lastName || undefined,
                };
              },
            );
        setPatientInfo(resolvedPatient);
        setQuestionnairePatientInfo?.(resolvedPatient);

        setValidationError(null);
        setLoading(false);
        if (isTreatmentJourney) {
          await synchronizeNextJourneyStep(resolvedPatient);
          journeyMachine.goNext();
        } else {
          nextQuestion();
        }
        return;
      } catch (error) {
        console.error("❌ Patient onboarding failed:", error);
        if (error?.code === PATIENT_AUTH_ERROR_CODES.required) {
          setLoading(false);
          setValidationError(null);
          setLoginInitialEmail(submittedEmail);
          if (isTreatmentJourney) {
            const authenticationIndex = visibleQuestions.findIndex(
              (question) => question.question_type === "patient_authentication",
            );
            setCurrentQuestionIndex(authenticationIndex >= 0 ? authenticationIndex : 0);
          } else {
            setShowLoginModal(true);
          }
          return;
        }
        setValidationError(
          error.message || "Failed to create account. Please try again."
        );
        setLoading(false);
        return;
      }
    }

    // Special handling for shipping address verification
    if (currentDisplayQuestion.question_type === "shipping_address") {
      if (isTreatmentJourney) {
        setLoading(true);
        try {
          let submittedAddress = currentResponse;
          const suggestion = await groupedQuestionRef.current?.suggestAddress?.(
            currentResponse,
          );
          if (
            suggestion
            && !reviewedAddressSuggestionsRef.current.has(
              suggestion.fingerprint,
            )
          ) {
            const useSuggested = await requestAddressSuggestionChoice(suggestion);
            reviewedAddressSuggestionsRef.current.add(suggestion.fingerprint);
            if (useSuggested) {
              submittedAddress = suggestion.values;
              currentResponse = suggestion.values;
              updateResponse(currentDisplayQuestion.id, suggestion.values);
            }
            setValidationError(null);
            setLoading(false);
            return;
          }
          completionResponses = buildCompletionResponseSnapshot({
            responses,
            latestResponses: getResponsesSnapshot(),
            currentQuestion: currentDisplayQuestion,
            currentResponse: submittedAddress,
          });
          await validateTreatmentLocation(
            completionResponses,
            { locationRole: currentDisplayQuestion.location_role },
          );
        } catch (error) {
          const reason = error?.details?.reason;
          const isShippingLocation = (
            currentDisplayQuestion.location_role === "shipping"
          );
          setValidationError(
            reason === "zip_state_mismatch"
              ? `ZIP code and state do not match. Please correct your ${
                isShippingLocation ? "shipping" : "service"
              } address.`
              : error?.message
                || `Please confirm or correct your ${
                  isShippingLocation ? "shipping" : "service"
                } address.`,
          );
          setLoading(false);
          return;
        }
        setLoading(false);
      } else if (
        groupedQuestionRef.current &&
        typeof groupedQuestionRef.current.verifyAddress === "function"
      ) {
        setLoading(true);
        try {
          const isValid = await groupedQuestionRef.current.verifyAddress(
            currentResponse
          );
          setLoading(false);
          if (!isValid) {
            setValidationError(
              `Address not recognized or not deliverable. Please verify your address before continuing.`
            );
            return;
          }
        } catch (error) {
          setLoading(false);
          setValidationError("Address verification failed. Please try again.");
          return;
        }
      }
    }

    let labCheckoutBoundarySubmitted = false;
    if (currentDisplayQuestion.question_type === "program_lab_checkout") {
      const labCheckout = visibleProgramLabCheckout(
        currentDisplayQuestion,
        questionnaire,
        latestResponses,
        { customProgram: isCustomProgramJourney, previewOnly: isPreviewMode },
      );
      const labStage = currentResponse?.lab_stage || "collection";
      const collectionPreflightError = getProgramLabCollectionError(
        labCheckout,
        currentResponse,
        { previewOnly: isPreviewMode, requireVerifiedAddress: false },
      );
      if (collectionPreflightError) {
        setValidationError(collectionPreflightError);
        setLoading(false);
        return;
      }
      if (
        labStage === "collection"
        && requiresProgramLabCheckout(labCheckout, currentResponse)
        && !isProgramLabAddressVerified(currentResponse)
      ) {
        setLoading(true);
        try {
          currentResponse = await verifyProgramLabAddress(
            currentResponse,
            (address) => verifyPatientAddress(address, {
              allowUnverifiedFallback: false,
            }),
          );
          updateResponse(currentDisplayQuestion.id, currentResponse);
        } catch (addressError) {
          setValidationError(
            addressError.message
            || "Patient address not recognized. Please check your street address, city, state, and ZIP code.",
          );
          setLoading(false);
          return;
        }
      }
      const collectionError = getProgramLabCollectionError(
        labCheckout,
        currentResponse,
        { previewOnly: isPreviewMode },
      );
      if (collectionError) {
        setValidationError(collectionError);
        setLoading(false);
        return;
      }

      // Program labs use one durable checkout response, but present the same
      // staged experience as standalone labs. Persisting the stage in that
      // response lets the shared questionnaire cursor remain stable while
      // preventing AOE questions from being rendered on the collection page.
      // AOE answers are still validated again before the runtime boundary is
      // submitted below.
      if (labStage === "collection") {
        const nextLabStage = hasProgramLabAoeQuestions(labCheckout, currentResponse)
          ? "aoe"
          : "consents";
        updateResponse(
          currentDisplayQuestion.id,
          { ...currentResponse, lab_stage: nextLabStage },
        );
        setValidationError(null);
        setLoading(false);
        return;
      }
      if (labStage === "aoe" && !isProgramLabAoeComplete(labCheckout, currentResponse)) {
        setValidationError(
          "Answer all required laboratory questions before continuing.",
        );
        return;
      }
      if (labStage === "aoe") {
        updateResponse(
          currentDisplayQuestion.id,
          { ...currentResponse, lab_stage: "consents" },
        );
        setValidationError(null);
        setLoading(false);
        return;
      }
      if (!isProgramLabConsentsComplete(labCheckout, currentResponse)) {
        setValidationError(
          "Accept all required laboratory consents before continuing.",
        );
        return;
      }
      if (!isProgramLabAoeComplete(labCheckout, currentResponse)) {
        setValidationError(
          "Answer all required laboratory questions before continuing.",
        );
        return;
      }
      const normalizedLabCheckout = normalizeProgramLabCheckout(
        currentResponse,
        labCheckout,
      );
      // updateResponse updates the ref synchronously, but the boundary
      // transition must still receive an explicit snapshot. The lab question
      // is dynamically projected into the questionnaire and can otherwise be
      // mistaken for a local screening checkpoint by the journey cursor.
      completionResponses = buildCompletionResponseSnapshot({
        responses,
        latestResponses: getResponsesSnapshot(),
        currentQuestion: currentDisplayQuestion,
        currentResponse: normalizedLabCheckout,
      });
      updateResponse(
        currentDisplayQuestion.id,
        normalizedLabCheckout,
      );
      labCheckoutBoundarySubmitted = true;
    }

    setValidationError(null);

    // Go to payment on last question OR complete questionnaire if it's a product selection
    const displayIsLastQuestion = activeProductSelectionGroup
      ? activeProductSelectionGroup.endIndex === visibleQuestions.length - 1
      : isLastQuestion();

    if (displayIsLastQuestion) {
      const finalStepAction = finalTreatmentStepAction(
        currentDisplayQuestion.question_type,
        isTreatmentJourney,
        questionnaire?.location_contract_version,
      );
      if (finalStepAction === FINAL_TREATMENT_STEP_ACTION.prepareCheckout) {
        console.log(
          "🚀 Treatment checkout selections complete; preparing checkout.",
        );
        await handleQuestionnaireComplete(completionResponses);
      } else if (
        finalStepAction === FINAL_TREATMENT_STEP_ACTION.useLegacyCompletion
        && shouldCompleteLegacyProductSelection(
          currentDisplayQuestion.question_type,
          isTreatmentJourney,
        )
      ) {
        // Production legacy behavior: order creation returns the durable
        // /checkout/<UUID> URL. Never show embedded payment after this step.
        console.log("🚀 Legacy product selected; creating checkout URL.");
        await handleQuestionnaireComplete();
      } else if (
        finalStepAction
        === FINAL_TREATMENT_STEP_ACTION.revealProductSelection
      ) {
        setLoading(true);
        try {
          const productSelectionAuthority = await synchronizeTreatmentAuthority(
            TREATMENT_RUNTIME_STATE.productSelection,
          );
          if (
            productSelectionAuthority?.state
            === TREATMENT_RUNTIME_STATE.disqualified
          ) {
            setLoading(false);
            return;
          }
          // The walk toward product selection stops at any boundary that still
          // needs patient input. Treatment consents are the multi-treatment
          // case: they only become visible once the runtime publishes its
          // recommendations, so the step that requested product selection was
          // judged "last" against a list that did not contain them yet.
          // Advance into whatever the refreshed projection revealed rather
          // than reporting that no options matched.
          if (
            productSelectionAuthority
            && productSelectionAuthority.state
              !== TREATMENT_RUNTIME_STATE.productSelection
            && nextQuestion(undefined, currentQuestion?.id)
          ) {
            setLoading(false);
            return;
          }
          const eligibleOptions = (
            productSelectionAuthority?.step?.programs || []
          ).flatMap((program) => (
            program.checkout_questions || []
          )).flatMap((question) => question.options || []);
          if (eligibleOptions.length === 0) {
            // Only blame availability when the runtime actually reached
            // product selection. Any earlier state means it is still waiting
            // on the patient, and saying "nothing matches" sends them off to
            // re-check answers that were never the problem.
            const stalledState = (
              productSelectionAuthority
              && productSelectionAuthority.state
                !== TREATMENT_RUNTIME_STATE.productSelection
            )
              ? productSelectionAuthority.state
              : null;
            setValidationError(
              stalledState === TREATMENT_RUNTIME_STATE.consents
                ? "Please accept the remaining treatment consents to continue."
                : stalledState
                  ? "We could not open your treatment options yet. Please complete the remaining steps and try again."
                  : isPreviewMode
                    ? "Preview cannot continue because this Program has no eligible configured treatment options. Review its checkout products, Treatment Types, visibility rules, and service-state availability."
                    : "No treatment options currently match your answers and confirmed care state. Review your answers or contact the care team.",
            );
            setLoading(false);
            return;
          }
          if (!navigateToTreatmentAuthorityState(
            productSelectionAuthority?.state,
          )) {
            nextQuestion(responses, currentQuestion?.id);
          }
        } catch (authorityError) {
          const operatorAction = isPreviewMode
            ? authorityError?.details?.corrective_action?.label
            : "";
          setValidationError(
            `${authorityError.message
              || "Unable to load the available treatment options."}${
              operatorAction ? ` ${operatorAction}.` : ""
            }`,
          );
          setLoading(false);
          return;
        }
        setLoading(false);
      } else if (effectiveRuntimeProgramId || effectiveRuntimeCustomProgramId) {
        setValidationError(
          "No products are currently available for this program. Please contact support.",
        );
      } else {
        setCurrentFlow(FLOW_STATES.PAYMENT);
      }
    } else {
      if (isTreatmentJourney) {
        setLoading(true);
        try {
          let nextAuthority;
          if (labCheckoutBoundarySubmitted) {
            // A lab checkout is a runtime boundary, never a local question
            // checkpoint. Resolve the next authored boundary explicitly so a
            // stale journey cursor cannot submit checkpoint_program_answers
            // and leave the server in treatment_screening.
            const currentIndex = visibleQuestions.findIndex((question) => (
              [
                currentDisplayQuestion.id,
                currentDisplayQuestion.source_id,
                currentDisplayQuestion.release_node_id,
              ].filter(Boolean).some((identity) => (
                question.id === identity
                || question.source_id === identity
                || question.release_node_id === identity
              ))
            ));
            const nextQuestion = currentIndex >= 0
              ? visibleQuestions[currentIndex + 1]
              : null;
            const targetState = (
              authorityStateForQuestion(nextQuestion)
              || TREATMENT_RUNTIME_STATE.productSelection
            );
            nextAuthority = await synchronizeTreatmentAuthority(
              targetState,
              completionResponses,
            );
          } else {
            nextAuthority = await synchronizeNextJourneyStep();
          }
          // synchronizeNextJourneyStep installs the authoritative projection
          // before returning. If the answer knocked the patient out, let that
          // projection drive the disqualification screen immediately; never
          // fall through to local positional navigation or expose later ID
          // verification steps.
          if (
            nextAuthority?.state === TREATMENT_RUNTIME_STATE.disqualified
          ) {
            setLoading(false);
            return;
          }
          if (
            nextAuthority
            && !ADVANCEABLE_POST_ANSWER_STATES.has(nextAuthority.state)
          ) {
            setLoading(false);
            return;
          }
          const currentBoundaryState = authorityStateForQuestion(
            currentDisplayQuestion,
          );
          if (
            nextAuthority?.state
            && nextAuthority.state !== currentBoundaryState
            && navigateToTreatmentAuthorityState(nextAuthority.state)
          ) {
            setLoading(false);
            return;
          }
        } catch (authorityError) {
          if (isRuntimePatientPermissionError(authorityError)) {
            const email = patientInfo?.email || authStateRef.current?.patient?.email || "";
            setLoginInitialEmail(email);
            await resetProgramPatientIdentity();
            const authenticationIndex = visibleQuestions.findIndex(
              (question) => question.question_type === "patient_authentication",
            );
            setCurrentQuestionIndex(authenticationIndex >= 0 ? authenticationIndex : 0);
            setValidationError(null);
            setLoading(false);
            return;
          }
          if (authorityError?.code === "required_answers_missing") {
            const target = resolveMissingAnswerTarget(
              journeySteps,
              authorityError.details?.missing,
            );
            if (target) journeyMachine.goTo(target.stepId, true);
            setValidationError(MISSING_ANSWER_MESSAGE);
            setLoading(false);
            return;
          }
          const continueWithout = blockedTreatmentAction(authorityError);
          setBlockedTreatment(null);
          if (continueWithout) {
            // One sibling cannot be sold and the rest still can. Do not send
            // the patient back to care location: changing state does not fix
            // a misconfigured treatment, and the real remedy is to drop this
            // one. Offer that as a button rather than as unactionable text.
            setBlockedTreatment(continueWithout);
            setValidationError(
              `${continueWithout.treatmentLabel || "One treatment"} isn't available right now. Your other treatments still are.`,
            );
            setLoading(false);
            return;
          }
          if (
            authorityError?.code === "care_location_invalid"
            || authorityError?.code === "care_location_unavailable"
          ) {
            const target = resolveCareLocationTarget(journeySteps);
            if (target) journeyMachine.goTo(target.stepId, true);
            setValidationError(
              `${authorityError.message || "Review your care location and treatment choices."}${
                authorityError.details?.corrective_action?.label
                  ? ` ${authorityError.details.corrective_action.label}.`
                  : ""
              }`,
            );
            setLoading(false);
            return;
          }
          setValidationError(
            authorityError.message
              || "Unable to continue this treatment flow.",
          );
          setLoading(false);
          return;
        }
        setLoading(false);
      }
      if (activeProductSelectionGroup) {
        if (isTreatmentJourney) {
          // Product questions in a treatment journey are rendered as one
          // grouped page. Advance from the last source question in that
          // group, rather than from the first one represented by
          // `currentQuestion`, so one click moves to the next displayed step.
          const groupEndQuestionId = visibleQuestions[
            activeProductSelectionGroup.endIndex
          ]?.id;
          nextQuestion(undefined, groupEndQuestionId || currentQuestion?.id);
        }
        else setCurrentQuestionIndex(activeProductSelectionGroup.endIndex + 1);
      } else {
        if (isTreatmentJourney) {
          nextQuestion(undefined, currentQuestion?.id);
        }
        else nextQuestion();
      }
    }
  }, [
    currentQuestion,
    currentDisplayQuestion,
    currentDisplayResponse,
    responses,
    validateResponse,
    isLastQuestion,
    nextQuestion,
    navigateToTreatmentAuthorityState,
    bmiError,
    handleQuestionnaireComplete,
    api,
    setPatientInfo,
    setQuestionnairePatientInfo,
    effectiveRuntimeProgramId,
    effectiveRuntimeCustomProgramId,
    isPreviewMode,
    isTreatmentJourney,
    journeyBoundary,
    journeyMachine,
    journeySteps,
    activeProductSelectionGroup,
    batchUpdateResponses,
    getResponsesSnapshot,
    visibleQuestions,
    setCurrentQuestionIndex,
    synchronizeNextJourneyStep,
    synchronizeTreatmentAuthority,
    isCustomProgramJourney,
    resetProgramPatientIdentity,
    requestAddressSuggestionChoice,
    updateResponse,
    hasProgramLabAoeQuestions,
    isProgramLabAoeComplete,
    isProgramLabAddressVerified,
    requiresProgramLabCheckout,
    getProgramLabCollectionError,
    isProgramLabConsentsComplete,
    validateTreatmentLocation,
    verifyProgramLabAddress,
  ]);

  const handlePrevious = useCallback(async () => {
    setValidationError(null);
    setError(null);
    // The Lab page is a single runtime boundary with three local stages.
    // Resolve its local stage before considering cross-boundary navigation;
    // otherwise Back from AOE/Consents incorrectly exits to an earlier
    // treatment phase.
    if (
      currentDisplayQuestion?.question_type === "program_lab_checkout"
      && ["aoe", "consents"].includes(responses?.[currentDisplayQuestion.id]?.lab_stage)
    ) {
      const labCheckout = visibleProgramLabCheckout(
        currentDisplayQuestion,
        questionnaire,
        responses,
        { customProgram: isCustomProgramJourney, previewOnly: isPreviewMode },
      );
      const currentStage = responses[currentDisplayQuestion.id].lab_stage;
      const previousStage = currentStage === "consents"
        && hasProgramLabAoeQuestions(labCheckout, responses[currentDisplayQuestion.id])
        ? "aoe"
        : "collection";
      updateResponse(currentDisplayQuestion.id, {
        ...responses[currentDisplayQuestion.id],
        lab_stage: previousStage,
      });
      return;
    }
    // Library consent pages share a local cursor. Inline Program consents
    // must rewind the server cursor so the next checkpoint advances from
    // the question actually displayed after Back.
    if (
      isTreatmentJourney
      && authorityStateForQuestion(currentDisplayQuestion)
        === TREATMENT_RUNTIME_STATE.consents
      && currentJourneyStep
      && previousTreatmentStep
      && authorityStateForQuestion(previousTreatmentStep.question)
        === treatmentAuthority?.state
    ) {
      navigateToStableQuestion(
        lastCustomProgramStepItemId(previousTreatmentStep),
        treatmentAuthority.state,
      );
      return;
    }
    if (
      hasPreviousTreatmentPhase
      && treatmentAuthority?.presentation_contract_version !== "1"
    ) {
      setLoading(true);
      try {
        const targetState = previousJourneyAuthorityState({
          previousTreatmentStep,
          previousVisibleState: previousVisibleAuthorityState({
            visibleQuestions,
            currentQuestion: currentDisplayQuestion,
            stateForQuestion: authorityStateForQuestion,
          }),
          fallbackState: TREATMENT_RUNTIME_STATE.treatmentScreening,
          currentState: treatmentAuthority?.state,
        });
        await synchronizeTreatmentAuthority(
          targetState,
          null,
          null,
          { allowBackward: true },
        );
        navigateToCurrentTreatmentAuthority();
      } catch (authorityError) {
        setValidationError(
          authorityError.message
            || "Unable to return to the previous treatment phase.",
        );
      } finally {
        setLoading(false);
      }
      return;
    }
    if (
      isTreatmentJourney
      && treatmentAuthority?.presentation_contract_version === "1"
      && treatmentAuthority.can_go_back === true
    ) {
      setLoading(true);
      try {
        const previousVisibleState = previousJourneyAuthorityState({
          previousTreatmentStep,
          previousVisibleState: previousVisibleAuthorityState({
            visibleQuestions,
            currentQuestion: currentDisplayQuestion,
            stateForQuestion: authorityStateForQuestion,
          }),
          fallbackState: treatmentAuthority.back_state,
          currentState: treatmentAuthority.state,
        });
        const backTargetNodeId = backTargetForPreviousTreatmentStep({
          previousVisibleState,
          previousTreatmentStep,
          stateForQuestion: authorityStateForQuestion,
        });
        await synchronizeTreatmentAuthority(
          previousVisibleState || treatmentAuthority.state,
          getResponsesSnapshot(),
          null,
          {
            allowBackward: true,
            ...(backTargetNodeId ? { backTargetNodeId } : {}),
          },
        );
        navigateToCurrentTreatmentAuthority();
      } catch (authorityError) {
        setValidationError(
          authorityError.message || "Unable to return to the previous step.",
        );
      } finally {
        setLoading(false);
      }
      return;
    }
    if (isTreatmentJourney && currentJourneyStep) {
      const previousStep = previousTreatmentStep;
      const transition = previousStep
        ? planStepTransition(
            journeySteps,
            currentJourneyStep.id,
            previousStep.id,
          )
        : null;
      const serverCursorBack = (
        treatmentAuthority?.presentation_contract_version === "1"
      );
      if (previousStep && (transition?.mode === "synchronize" || serverCursorBack)) {
        setLoading(true);
        try {
          const backTargetNodeId = backTargetForPreviousTreatmentStep({
            previousVisibleState: authorityStateForQuestion(previousStep.question),
            previousTreatmentStep: previousStep,
            stateForQuestion: authorityStateForQuestion,
          });
          await synchronizeTreatmentAuthority(
            authorityStateForQuestion(previousStep.question),
            getResponsesSnapshot(),
            null,
            {
              allowBackward: true,
              ...(backTargetNodeId ? { backTargetNodeId } : {}),
            },
          );
        } catch (authorityError) {
          if (isRuntimePatientPermissionError(authorityError)) {
            const email = patientInfo?.email || authStateRef.current?.patient?.email || "";
            setLoginInitialEmail(email);
            await resetProgramPatientIdentity();
            const authenticationIndex = visibleQuestions.findIndex(
              (question) => question.question_type === "patient_authentication",
            );
            setCurrentQuestionIndex(authenticationIndex >= 0 ? authenticationIndex : 0);
            setLoading(false);
            return;
          }
          if (authorityError?.code === "required_answers_missing") {
            // Back navigation must not be blocked by validation for the
            // boundary we are leaving. The user can return and complete the
            // missing answer later.
            setValidationError(null);
            setLoading(false);
            if (isCustomProgramJourney && previousStep) {
              navigateToStableQuestion(
                lastCustomProgramStepItemId(previousStep),
                authorityStateForQuestion(previousStep.question),
              );
            } else {
              journeyMachine.goBack();
            }
            return;
          }
          setValidationError(
            authorityError.message
              || "Unable to return to the previous step.",
          );
          setLoading(false);
          return;
        }
        setLoading(false);
        if (serverCursorBack) {
          navigateToCurrentTreatmentAuthority();
          return;
        }
      }
      if (isCustomProgramJourney && previousStep) {
        // A projected step can contain multiple released items (for example
        // grouped product choices). Back must land on the last item in the
        // previous step, not its first item. The stable-navigation revision
        // also handles same-authority moves where no server projection is
        // replaced.
        navigateToStableQuestion(
          lastCustomProgramStepItemId(previousStep),
          authorityStateForQuestion(previousStep.question),
        );
      } else {
        journeyMachine.goBack();
      }
      return;
    }
    if (Array.isArray(visibleQuestions) && currentQuestionIndex > 0) {
      const previousVisible = visibleQuestions[currentQuestionIndex - 1];
      if (previousVisible?.question_type === "product_selection") {
        let targetIndex = currentQuestionIndex - 1;
        while (
          targetIndex > 0 &&
          visibleQuestions[targetIndex - 1]?.question_type === "product_selection"
        ) {
          targetIndex -= 1;
        }
        setCurrentQuestionIndex(targetIndex);
        return;
      }
    }
    previousQuestion();
  }, [
    currentJourneyStep,
    currentQuestionIndex,
    isCustomProgramJourney,
    isTreatmentJourney,
    journeySteps,
    journeyMachine,
    previousTreatmentStep,
    previousQuestion,
    responses,
    getResponsesSnapshot,
    patientInfo,
    resetProgramPatientIdentity,
    setCurrentQuestionIndex,
    synchronizeTreatmentAuthority,
    navigateToStableQuestion,
    currentDisplayQuestion,
    treatmentAuthority,
    hasProgramLabAoeQuestions,
    hasPreviousTreatmentPhase,
    navigateToCurrentTreatmentAuthority,
    synchronizeTreatmentAuthority,
    updateResponse,
    visibleQuestions,
  ]);

  const handleBackFromTreatmentCheckout = useCallback(async () => {
    if (!isTreatmentJourney) {
      setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
      return;
    }
    if (!treatmentOrderSummary) {
      if (Number.isInteger(checkoutReturnQuestionIndex)) {
        setCurrentQuestionIndex(checkoutReturnQuestionIndex);
      }
      setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
      setPaymentError(null);
      setValidationError(
        "Checkout was not prepared. Review your treatment selection and try again.",
      );
      return;
    }
    setLoading(true);
    setPaymentError(null);
    setValidationError(null);
    try {
      await synchronizeTreatmentAuthority(
        TREATMENT_RUNTIME_STATE.productSelection,
        getResponsesSnapshot(),
        null,
        { preservePendingResponses: true, allowBackward: true },
      );
      if (Number.isInteger(checkoutReturnQuestionIndex)) {
        setCurrentQuestionIndex(checkoutReturnQuestionIndex);
      }
      setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
    } catch (authorityError) {
      setPaymentError(
        authorityError.message || "Unable to return to product selection.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    FLOW_STATES.QUESTIONNAIRE,
    checkoutJourneyStep.previousIndex,
    checkoutReturnQuestionIndex,
    isTreatmentJourney,
    getResponsesSnapshot,
    setCurrentQuestionIndex,
    synchronizeTreatmentAuthority,
    treatmentOrderSummary,
  ]);

  const [authState, setAuthState] = useState({
    authenticated: isFollowUp || initialProgramAuth?.authenticated || false,
    sessionId: initialProgramAuth?.sessionId || null,
    accessToken: followUpAccessToken || initialProgramAuth?.accessToken || null,
    patient: prefillData?.patient || initialProgramAuth?.patient || null,
    prefillData: prefillData || initialProgramAuth?.prefillData || null,
  });
  authStateRef.current = authState;

  // Keep authState in sync with prefillData prop (for Portal redirections)
  useEffect(() => {
    if (prefillData && isFollowUp) {
      console.log(
        "[QuestionnaireForm] Syncing authState with prefillData prop"
      );
      setAuthState((prev) => ({
        ...prev,
        authenticated: true,
        accessToken: followUpAccessToken || prev.accessToken,
        prefillData: prefillData,
        patient: prefillData.patient || prev.patient,
      }));

      if (prefillData.patient && setQuestionnairePatientInfo) {
        setQuestionnairePatientInfo(prefillData.patient);
      }

      setPrefillSourcesForVisibility(prefillData.prefill_sources || {});
    }
  }, [
    prefillData,
    isFollowUp,
    followUpAccessToken,
    setQuestionnairePatientInfo,
  ]);

  // Load a missing shipping address once after authentication without delaying login.
  useEffect(() => {
    if (!authState.authenticated || !authState.accessToken || isPreviewMode) {
      return;
    }
    const currentPatient = authState.patient;
    const hasCompleteAddress = (address) =>
      [address?.address, address?.city, address?.state, address?.zip_code]
        .every((value) => String(value ?? "").trim());
    const hasAddress = hasCompleteAddress(currentPatient);
    const hasPrefillShipping = hasCompleteAddress(authState.prefillData?.shipping_address);
    if (hasAddress || hasPrefillShipping) {
      return;
    }
    if (patientProfileFetchAttemptRef.current === authState.accessToken) {
      return;
    }
    patientProfileFetchAttemptRef.current = authState.accessToken;

    let isMounted = true;
    fetchPatientProfile({
      accessToken: authState.accessToken,
      apiBaseUrl: runtimeApiBaseUrl,
    })
      .then((profileData) => {
        if (!isMounted || !profileData) return;
        setAuthState((prev) => {
          const mergedPatient = {
            ...prev.patient,
            ...Object.fromEntries(
              ["address", "address_line_2", "city", "state", "zip_code"].map((field) => [
                field,
                prev.patient?.[field] || profileData[field] || "",
              ]),
            ),
            patient_id: prev.patient?.patient_id || profileData.id,
          };
          const shippingAddress = {
            address: mergedPatient.address || "",
            address_line_2: mergedPatient.address_line_2 || "",
            city: mergedPatient.city || "",
            state: mergedPatient.state || "",
            zip_code: mergedPatient.zip_code || "",
          };
          return {
            ...prev,
            patient: mergedPatient,
            prefillData: {
              ...(prev.prefillData || {}),
              patient: mergedPatient,
              shipping_address: shippingAddress,
            },
          };
        });
        if (setQuestionnairePatientInfo) {
          setQuestionnairePatientInfo((prev) => {
            const current = prev || {};
            return {
              ...profileData,
              ...current,
              ...Object.fromEntries(
                ["address", "address_line_2", "city", "state", "zip_code"].map((field) => [
                  field,
                  current[field] || profileData[field] || "",
                ]),
              ),
              id: current.id || profileData.id,
            };
          });
        }
      })
      .catch(() => {
        console.warn("Could not fetch patient profile for shipping address prefill.");
      });

    return () => {
      isMounted = false;
    };
  }, [
    authState.authenticated,
    authState.accessToken,
    authState.patient,
    authState.prefillData,
    isPreviewMode,
    runtimeApiBaseUrl,
    setQuestionnairePatientInfo,
  ]);

  // A program patient change owns a fresh response/runtime boundary.
  const handleLoginSuccess = useCallback(
    async (authData) => {
      const previousPatient = authStateRef.current?.patient;
      if (
        isTreatmentJourney
        && !isPreviewMode
        && previousPatient
        && !samePatientIdentity(previousPatient, authData.patient)
      ) {
        await resetProgramPatientIdentity();
      }
      const nextAuthState = {
        authenticated: true,
        sessionId: authData.sessionId,
        accessToken: authData.accessToken,
        patient: authData.patient,
        prefillData: authData.prefillData,
      };
      authStateRef.current = nextAuthState;
      setAuthState(nextAuthState);

      // Update patient info in the hook
      if (authData.patient && setQuestionnairePatientInfo) {
        setQuestionnairePatientInfo(authData.patient);
      }
      setPrefillSourcesForVisibility(authData.prefillData?.prefill_sources || {});
    },
    [
      isPreviewMode,
      isTreatmentJourney,
      resetProgramPatientIdentity,
      setQuestionnairePatientInfo,
    ]
  );

  /**
   * The saved session is bound to a patient and the tab holds no token for
   * them. Sign them in, then resume that same session from its kept pointer.
   */
  const { differentAccount, markSignInAttempt } = useDifferentAccountNotice({
    errorCode: treatmentRuntimeError?.code,
    loading: questionnaireLoading,
  });
  const handleResumeSignIn = useCallback(
    (authData) => {
      markSignInAttempt();
      return completeResumeSignIn({
        authData,
        applyLogin: handleLoginSuccess,
        storeAuthentication: storeProgramAuthentication,
        resume: () => {
          setError(null);
          return retryQuestionnaireInitialization(LAUNCH_REASON.resumeAfterSignIn);
        },
      });
    },
    [handleLoginSuccess, markSignInAttempt, retryQuestionnaireInitialization, setError],
  );

  /** Store the patient identity and bind it to the current runtime session. */
  const handleIntakeAuthenticated = useCallback(
    async ({
      patient,
      accessToken,
      accessTokenExpiresIn,
      profile,
      restored = false,
      profileUpdated = false,
    }) => {
      const enrichedPatient = { ...(patient || {}) };

      const shippingAddress = enrichedPatient?.address ? {
        address: enrichedPatient.address,
        address_line_2: enrichedPatient.address_line_2 || "",
        city: enrichedPatient.city,
        state: enrichedPatient.state,
        zip_code: enrichedPatient.zip_code,
      } : null;

      const prefillPayload = {
        patient: enrichedPatient,
        profile,
        ...(shippingAddress ? { shipping_address: shippingAddress } : {}),
      };

      await handleLoginSuccess({
        sessionId: null,
        accessToken,
        patient: enrichedPatient,
        prefillData: prefillPayload,
      });
      updateTreatmentPreviewProfile(patient);
      // A changed patient resets the previous runtime and clears its stored
      // credentials inside handleLoginSuccess. Persist the new identity only
      // after that reset so a runtime reprojection can restore this login.
      if (!isPreviewMode && !restored) {
        if (profileUpdated) {
          updateProgramAuthenticationProfile({ patient: enrichedPatient, profile });
        } else {
          storeProgramAuthentication({
            accessToken,
            accessTokenExpiresIn,
            patient: enrichedPatient,
            profile,
          });
        }
      }
      let bound;
      const recoverCareLocation = (error, projection = null) => {
        if (
          !["care_location_invalid", "care_location_unavailable"].includes(
            error?.code,
          )
        ) {
          return null;
        }
        const target = resolveCareLocationTarget(journeySteps);
        if (!target) return null;
        const targetStep = journeySteps.find((step) => step.id === target.stepId);
        if (!targetStep) return null;
        setCurrentQuestionIndex(targetStep.startIndex);
        return {
          disposition: AUTH_BIND_DISPOSITION.careLocationRequired,
          profile: projection?.profile || profile || null,
          projection,
        };
      };
      try {
        bound = await bindTreatmentPatientAuthority(accessToken, {
          refreshProfile: profileUpdated,
        });
      } catch (error) {
        const recovered = recoverCareLocation(error, bound);
        if (recovered) return recovered;
        if (profileUpdated) throw error;
        setLoginInitialEmail(patient?.email || "");
        clearProgramAuthentication();
        const clearedAuthState = {
          authenticated: false,
          sessionId: null,
          accessToken: null,
          patient: null,
          prefillData: null,
        };
        authStateRef.current = clearedAuthState;
        setAuthState(clearedAuthState);
        await resetProgramPatientIdentity();
        throw error;
      }
      // Binding and routing are separate authority mutations. A routing
      // conflict must retain the successfully bound session so the installed
      // projection can be reconciled or retried without changing identity.
      try {
        return await completePostBindAuthority({
          bound,
          expectedState: authorityStateForQuestion(
            journeySteps.find(
              (step) => step.id === currentJourneyStep?.nextTargetId,
            )?.question,
          ),
          fallbackProfile: profile,
          patient,
          synchronizeNextJourneyStep,
        });
      } catch (error) {
        const recovered = recoverCareLocation(error, bound);
        if (recovered) return recovered;
        throw error;
      }
    },
    [
      bindTreatmentPatientAuthority,
      currentJourneyStep?.nextTargetId,
      handleLoginSuccess,
      isPreviewMode,
      journeySteps,
      resetProgramPatientIdentity,
      setCurrentQuestionIndex,
      synchronizeNextJourneyStep,
      updateTreatmentPreviewProfile,
    ],
  );

  /** Advance past the boundary once the profile is complete. */
  const [pendingAuthenticationAdvance, setPendingAuthenticationAdvance] = useState(false);
  const [pendingAuthenticationTargetId, setPendingAuthenticationTargetId] = useState(null);
  const handleIntakeAuthenticationComplete = useCallback(() => {
    if (!claimAuthenticationAdvance(intakeAuthenticationAdvancedRef)) return;
    // A capability preview can retain a routing projection after simulated
    // authentication. Resolve against the hook's current authority snapshot,
    // but reject the completed authentication boundary as a destination so
    // the local routing/next-question fallback can take over.
    if (navigateToCurrentTreatmentAuthority?.({
      preferTreatmentIntro: true,
      ...(isPreviewMode
        ? { excludedQuestionTypes: ["patient_authentication"] }
        : {}),
    })) return;
    // The runtime starts in routing whenever the release contains an authored
    // routing boundary. A custom-program questionnaire can contain several
    // routing questions, so target the first question owned by that boundary
    // instead of jumping to a later Service Area Check node.
    const routingQuestion = firstPostAuthenticationRoutingQuestion(visibleQuestions);
    if (routingQuestion?.id) {
      setPendingAuthenticationTargetId(routingQuestion.id);
      return;
    }
    setPendingAuthenticationAdvance(true);
  }, [
    isPreviewMode,
    navigateToCurrentTreatmentAuthority,
    visibleQuestions,
  ]);

  // The claim above can be requested while a rehydration/load effect is
  // still (or again) in flight - re-check live state via an effect
  // instead of the value closed over when this callback was created, so
  // a resolved-but-stale isRehydrating/questionnaireLoading never drops
  // the advance permanently.
  useEffect(() => {
    if (!pendingAuthenticationAdvance && !pendingAuthenticationTargetId) return;
    if (isRehydrating || questionnaireLoading) return;
    setPendingAuthenticationAdvance(false);
    if (pendingAuthenticationTargetId) {
      const targetIndex = visibleQuestions.findIndex(
        (question) => question?.id === pendingAuthenticationTargetId,
      );
      setPendingAuthenticationTargetId(null);
      setCurrentQuestionIndex((currentIndex) => (
        targetIndex >= 0 ? targetIndex : currentIndex + 1
      ));
      return;
    }
    setCurrentQuestionIndex((index) => index + 1);
  }, [
    pendingAuthenticationAdvance,
    pendingAuthenticationTargetId,
    isRehydrating,
    questionnaireLoading,
    setCurrentQuestionIndex,
    visibleQuestions,
  ]);

  const handleProgramIdentityChange = useCallback(async () => {
    setLoginInitialEmail("");
    await resetProgramPatientIdentity();
  }, [resetProgramPatientIdentity]);

  useEffect(() => {
    if (!isFollowUp || !authState.authenticated || !followUpAccessToken) {
      return;
    }

    const currentPrefill = authState.prefillData || {};
    const hasExplicitPrefill =
      currentPrefill.prefill_responses &&
      Object.keys(currentPrefill.prefill_responses).length > 0;

    if (hasExplicitPrefill || followUpPrefillRefreshAttempted.current) {
      return;
    }

    followUpPrefillRefreshAttempted.current = true;

    (async () => {
      try {
        const startResult = await followUpService.startQuestionnaire();
        const refreshedPrefill = {
          ...(startResult.prefill_data || {}),
          patient: startResult.patient || currentPrefill.patient || null,
        };

        setAuthState((prev) => ({
          ...prev,
          authenticated: true,
          accessToken: followUpAccessToken || prev.accessToken,
          prefillData: refreshedPrefill,
          patient: refreshedPrefill.patient || prev.patient,
        }));

        setPrefillSourcesForVisibility(
          refreshedPrefill.prefill_sources || {}
        );
      } catch (err) {
        console.warn(
          "[QuestionnaireForm] Follow-up prefill refresh failed:",
          err?.message || err
        );
      }
    })();
  }, [isFollowUp, authState.authenticated, authState.prefillData, followUpAccessToken]);

  // Apply explicit prefill_responses from backend first
  useEffect(() => {
    if (!authState.authenticated || !authState.prefillData || !questionnaire?.questions) {
      return;
    }

    const explicitPrefill = authState.prefillData.prefill_responses || {};
    const updates = {};

    Object.entries(explicitPrefill).forEach(([questionId, value]) => {
      if (prefilledQuestions.current.has(questionId)) return;
      if (responses[questionId]) return;
      updates[questionId] = value;
      prefilledQuestions.current.add(questionId);
    });

    if (Object.keys(updates).length > 0) {
      batchUpdateResponses(updates);
    }
  }, [authState.authenticated, authState.prefillData, questionnaire, responses, batchUpdateResponses]);

  // Prefill standalone questions: DOB, Sex, Medical conditions, Medications, Allergies
  // Collects all prefills first, then updates responses in one batch to avoid stale state
  useEffect(() => {
    if (
      !authState.authenticated ||
      !authState.prefillData ||
      !questionnaire?.questions
    ) {
      return;
    }

    const prefillData = authState.prefillData;
    const prefillUpdates = {}; // Collect all prefills here

    // Non-prefillable question types
    const NON_PREFILLABLE_TYPES = [
      "personal_details",
      "shipping_address",
      "file_upload",
      "consent",
      "checkout",
      "single_choice",
      "multiple_choice",
      "height_weight",
      "bmi",
      "product_selection",
      "medication_dose_selector",
    ];

    questionnaire.questions.forEach((q) => {
      // Skip if already prefilled or has response
      if (prefilledQuestions.current.has(q.id)) return;
      if (responses[q.id]) return;
      if (NON_PREFILLABLE_TYPES.includes(q.question_type)) return;

      let prefillValue = null;
      const questionType = q.question_type;
      const questionText = (q.question_text || "").toLowerCase();

      // 1. Try exact question_type match first
      if (questionType === "sex") {
        prefillValue = prefillData?.personal_details?.sex;
      } else if (questionType === "medical_conditions") {
        prefillValue = prefillData?.medical_info?.medical_conditions;
      } else if (questionType === "self_reported_meds") {
        prefillValue = prefillData?.medical_info?.self_reported_meds;
      } else if (questionType === "allergies") {
        prefillValue = prefillData?.medical_info?.allergies;
      } else if (questionType === "state_routing") {
        prefillValue = isTreatmentJourney
          ? prefillData?.personal_details?.state
          : (
            prefillData?.shipping_address?.state ||
            prefillData?.personal_details?.state
          );
      }

      // 2. Text-based matching for specific patterns
      if (!prefillValue) {
        if (
          questionText === "date of birth" ||
          questionText.startsWith("date of birth")
        ) {
          prefillValue = prefillData?.personal_details?.date_of_birth;
        } else if (
          questionText.includes("sex assigned at birth") ||
          questionText.includes("sex at birth")
        ) {
          prefillValue = prefillData?.personal_details?.sex;
        } else if (
          questionText.includes("your current medical conditions") ||
          questionText.includes("identify all your current medical")
        ) {
          prefillValue = prefillData?.medical_info?.medical_conditions;
        } else if (
          questionText.includes("your current medications") ||
          questionText.includes("list all your current medications")
        ) {
          prefillValue = prefillData?.medical_info?.self_reported_meds;
        } else if (
          questionText.includes("your known allergies") ||
          questionText.includes("list all of your known allergies")
        ) {
          prefillValue = prefillData?.medical_info?.allergies;
        }
      }

      // 3. Add to batch if we found a value
      if (
        prefillValue !== null &&
        prefillValue !== undefined &&
        prefillValue !== ""
      ) {
        prefilledQuestions.current.add(q.id);
        prefillUpdates[q.id] = prefillValue;
      }
    });

    // 3. Apply explicit prefill_responses from backend (question-level)
    if (prefillData?.prefill_responses) {
      Object.entries(prefillData.prefill_responses).forEach(([questionId, value]) => {
        if (prefilledQuestions.current.has(questionId)) return;
        if (responses[questionId]) return;
        if (value === null || value === undefined || value === "") return;

        prefilledQuestions.current.add(questionId);
        prefillUpdates[questionId] = value;
      });
    }

    // 4. Batch update all prefills at once
    if (Object.keys(prefillUpdates).length > 0) {
      batchUpdateResponses(prefillUpdates);
    }
  }, [
    authState.authenticated,
    authState.prefillData,
    questionnaire,
    batchUpdateResponses,
    responses,
    isTreatmentJourney,
  ]);

  const renderQuestion = useCallback(
    (question) => {
      if (!question) return null;

      // A dose-scoped question must never fall back to the full visit-config
      // catalog - an empty list means "no products at this dose level".
      const checkoutCfg =
        question.validation?.checkout_config || question.checkout_config;
      const isDoseScoped = Boolean(
        checkoutCfg?.dose_mapping || checkoutCfg?.dose_mapping_label
      );

      // Use visit config medicines for product_selection so all products (e.g. 5ml and 10ml) are shown
      let q = question.question_type === "program_authentication_preview"
        ? {
            ...question,
            question_type: "personal_details",
            question_text: "Personal Details",
          }
        : question;
      if (
        question.question_type === "product_selection" &&
        !isDoseScoped &&
        visitConfig?.medicines?.length &&
        (!Array.isArray(question.answer_choices) || question.answer_choices.length === 0)
      ) {
        q = {
          ...question,
          answer_choices: buildProductChoicesFromMedicines(visitConfig.medicines),
        };
      }

      const questionValidationError = getQuestionValidationError(q.id);

      const isActiveGroupedProductSelection =
        activeProductSelectionGroup &&
        question.question_type === "product_selection" &&
        String(question.id) === String(activeProductSelectionGroup.syntheticQuestion.id);

      const commonProps = {
        question: q,
        value: isActiveGroupedProductSelection ? currentDisplayResponse : responses[q.id],
        onChange: (value) => {
          if (!isActiveGroupedProductSelection) {
            handleResponseChange(q.id, value);
            return;
          }

          setValidationError(null);
          setError(null);

          const selectedItems = dedupeProductSelectionItems(Array.isArray(value)
            ? value
            : (value ? [value] : []));
          const groupedUpdates = partitionProductSelectionsByQuestion(
            activeProductSelectionGroup.questions,
            selectedItems,
          );

          batchUpdateResponses(groupedUpdates);
        },
        error: questionValidationError,
        onAgeError: (error) => setDobError(error),
        patientInfo: authState.patient || patientInfo,
        templateId,
        clientConfig: effectiveClientConfig,
        questionnaire,
        isAuthenticated: authState.authenticated,
        onLoginSuccess: handleLoginSuccess,
        continueTreatmentWithEligiblePrograms,
        treatmentAuthority,
        setShowLoginModal: (show, email = "") => {
          setShowLoginModal(show);
          if (email) setLoginInitialEmail(email);
        },
      };

      switch (q.question_type) {
        case "matched_program_selection":
          return (
            <MatchedProgramsQuestion
              {...commonProps}
              allResponses={responses}
              synchronizeAuthority={synchronizeTreatmentAuthority}
              isCustomProgramJourney={isCustomProgramJourney}
            />
          );
        case "treatment_screening_intro":
          return <TreatmentScreeningIntro {...commonProps} />;
        case "single_choice":
        case "sex":
        case "labs":
          return <SingleChoice {...commonProps} />;
        case "multiple_choice":
          return <MultipleChoice {...commonProps} />;
        case "height_weight":
        case "bmi":
          return (
            <HeightWeight
              {...commonProps}
              onBMIError={setBMIError}
              questionnaire={questionnaire}
              updateResponse={updateResponse}
              allResponses={responses}
              previewOnly={isPreviewMode}
            />
          );
        case "number":
          return <NumberInput {...commonProps} />;
        case "patient_authentication":
          return (
            <TreatmentAuthenticationStep
              key={isPreviewMode ? previewIdentity : patientIdentityRevision}
              brandName={getCompanyName()}
              onAuthenticated={handleIntakeAuthenticated}
              onIdentityReset={handleProgramIdentityChange}
              onReady={handleIntakeAuthenticationComplete}
              previewMode={isPreviewMode}
              previewExistingPatient={
                previewIdentity === PREVIEW_PROTOCOL.identities.existingPatient
              }
              previewPatientProfile={
                isPreviewMode ? previewPatientDefaults(questionnaire) : null
              }
              apiBaseUrl={runtimeApiBaseUrl}
              initialAuthentication={
                !isPreviewMode && authState.authenticated
                  ? {
                      patient: authState.patient,
                      accessToken: authState.accessToken,
                      accessTokenExpiresIn:
                        initialProgramAuth?.accessTokenExpiresIn,
                      profile: authState.prefillData?.profile,
                    }
                  : null
              }
              initialEmail={loginInitialEmail}
            />
          );
        case "personal_details":
          return (
            <GroupedQuestion
              key={isPreviewMode
                ? previewIdentity
                : isTreatmentJourney
                  ? `${patientIdentityKey(authState.patient) || "anonymous"}:${patientIdentityRevision}`
                  : undefined}
              {...commonProps}
              authenticatedPatient={authState.patient}
              prefillData={authState.prefillData}
              ref={groupedQuestionRef}
              previewOnly={isPreviewMode}
              refreshPrefillOnPatientChange={isTreatmentJourney}
            />
          );
        case "shipping_address":
          // Legacy questionnaires may continue to reuse their care/state
          // answer for shipping. Treatment journeys keep shipping state as a
          // separate value because delivery and care location can differ.
          const stateRoutingResponse = !isTreatmentJourney
            ? Object.entries(responses).find(
              ([key, val]) => {
                const stateRoutingQ = questionnaire?.questions?.find(
                  (q) => q.id === key && q.question_type === "state_routing"
                );
                return stateRoutingQ && val;
              }
            )
            : null;
          const preSelectedState = stateRoutingResponse
            ? stateRoutingResponse[1]
            : null;
          return (
            <GroupedQuestion
              key={isPreviewMode
                ? previewIdentity
                : isTreatmentJourney
                  ? `${patientIdentityKey(authState.patient) || "anonymous"}:${patientIdentityRevision}:${q.id}`
                  : q.id}
              {...commonProps}
              preSelectedState={preSelectedState}
              authenticatedPatient={authState.patient}
              prefillData={authState.prefillData}
              ref={groupedQuestionRef}
              previewOnly={isPreviewMode}
              refreshPrefillOnPatientChange={isTreatmentJourney}
            />
          );
        case "textarea":
        case "text":
        case "date":
        case "email":
        case "state":
        case "zip":
          // All text-based inputs use TextInput component
          return <TextInput {...commonProps} />;
        case "phone":
          // Phone numbers use dedicated PhoneNumberInput with country flag
          return <PhoneNumberInput {...commonProps} />;
        case "consent":
          return <ConsentForm {...commonProps} />;
        case "program_lab_checkout":
          return (
            <TreatmentLabCheckoutStep
              {...commonProps}
              formError={validationError || ""}
              questionnaire={questionnaire}
              allResponses={responses}
              previewOnly={isPreviewMode}
              authority={treatmentAuthority}
              customProgram={isCustomProgramJourney}
            />
          );
        case "file_upload":
          return (
            <FileUpload
              {...commonProps}
              removeFile={removeQuestionnaireFile}
              uploadFile={
                isTreatmentJourney ? uploadTreatmentRuntimeFile : undefined
              }
            />
          );
        case "checkout":
          {
            const checkoutChoices = Array.isArray(q.answer_choices) && q.answer_choices.length > 0
              ? q.answer_choices
              : (Array.isArray(q.checkout_config?.products) ? q.checkout_config.products : []);
            if (checkoutChoices.length > 0) {
              return (
                <SingleChoice
                  {...commonProps}
                  isTreatmentJourney={isTreatmentJourney}
                  question={{
                    ...q,
                    question_type: "product_selection",
                    answer_choices: checkoutChoices,
                  }}
                />
              );
            }
          }
          return (
            <CheckoutQuestion
              checkoutConfig={q.checkout_config}
              couponCode={couponCode}
              onContinue={(response) =>
                handleResponseChange(q.id, response)
              }
            />
          );
        case "product_selection":
          if (activeProductSelectionGroup) {
            return (
              <TreatmentProductSelection
                {...commonProps}
                customProgram={isCustomProgramJourney}
                serviceState={getSelectedStateCode(questionnaire, responses, true) || ""}
              />
            );
          }
          // Apply pharmacy routing in UI using backend-provided routing metadata.
          // This ensures excluded states only see products for the resolved pharmacy.
          const selectedStateCode = getSelectedStateCode(
            questionnaire,
            responses,
            isTreatmentJourney,
          );
          let rawAnswerChoices = Array.isArray(q.answer_choices) && q.answer_choices.length > 0
            ? q.answer_choices
            : (Array.isArray(q.checkout_config?.products) ? q.checkout_config.products : []);
            
          // Expand choices to include all variants if the choice is a base product
          const answerChoices = [];
          rawAnswerChoices.forEach((choice) => {
            if (choice && choice.product_id && visitConfig?.medicines) {
              const medicine = visitConfig.medicines.find(
                (m) => String(m.id) === String(choice.product_id)
              );
              if (medicine && Array.isArray(medicine.variants) && medicine.variants.length > 0) {
                medicine.variants
                  .filter((v) => v.is_active !== false)
                  .forEach((v) => {
                    answerChoices.push({
                      ...choice,
                      id: v.id,
                      variant_id: v.id,
                      name: v.name || choice.product_name || choice.name,
                      quantity: v.quantity,
                      final_price: v.final_price,
                      original_price:
                        v.total_price !== v.final_price ? v.total_price : undefined,
                      total: v.total_price,
                      shipping_fee: v.shipping_fee,
                      price: v.final_price,
                      days_supply: v.quantity,
                      refills: v.refills ?? medicine.refills,
                    });
                  });
                return;
              }
            }
            answerChoices.push(choice);
          });
          const hasAnyChoiceWithPharmacy = answerChoices.some(
            (choice) =>
              choice &&
              typeof choice === "object" &&
              choice.pharmacy_id !== null &&
              choice.pharmacy_id !== undefined &&
              String(choice.pharmacy_id).trim() !== ""
          );
          const hasAnyChoiceWithServiceStates = answerChoices.some(
            (choice) =>
              choice &&
              typeof choice === "object" &&
              Array.isArray(choice.service_states) &&
              normalizeServiceStateCodes(choice.service_states).length > 0
          );

          const filteredChoices = answerChoices.filter((choice) => {
            if (choice && typeof choice === "object" && choice.is_active === false) {
              return false;
            }
            const productVisibility = firstProgramRuleSet(
              choice?.visibilityRules,
              choice?.visibility_rules,
              choice?.conditional_logic
            );
            if (
              hasProgramRuleContent(productVisibility) &&
              !evaluateProgramVisibility(productVisibility, responses)
            ) {
              return false;
            }
            if (!shouldDisplayChoiceForServiceState(choice, selectedStateCode)) {
              return false;
            }
            return shouldDisplayChoiceForRouting(
              choice,
              q.routing_rules,
              selectedStateCode
            );
          });

          // Safety fallback:
          // keep strict routing whenever pharmacy metadata exists, but avoid showing
          // an empty product step when legacy/stale choices lack pharmacy linkage.
          const shouldFallbackToUnfiltered =
            q.routing_rules &&
            selectedStateCode &&
            filteredChoices.length === 0 &&
            !hasAnyChoiceWithPharmacy &&
            !hasAnyChoiceWithServiceStates;

          const finalChoices = shouldFallbackToUnfiltered
            ? answerChoices
            : filteredChoices;

          if (finalChoices.length === 0) {
            // Distinguish the two empty states:
            //  - no active choices at all -> nothing configured at this dose level
            //  - active choices existed   -> dose has products, but none reachable
            //                                from the patient's state / routed pharmacy
            const activeChoices = answerChoices.filter(
              (choice) => !(choice && typeof choice === "object" && choice.is_active === false)
            );
            const blockedByRouting = activeChoices.length > 0;
            const doseLabel = checkoutCfg?.dose_mapping_label;

            console.warn("[product_selection] no selectable products", {
              questionId: q.id,
              doseScoped: isDoseScoped,
              dose: doseLabel || null,
              received: answerChoices.length,
              active: activeChoices.length,
              state: selectedStateCode || null,
            });

            return (
              <ErrorCard
                variant="error"
                title={
                  blockedByRouting
                    ? "Not available in your state"
                    : "No matching products found"
                }
                message={
                  blockedByRouting
                    ? `We can't currently ship ${
                        doseLabel || "this medication"
                      } to ${selectedStateCode || "your location"}. Please contact support to continue.`
                    : `We don't have any products available${
                        doseLabel ? ` for ${doseLabel}` : ""
                      } right now. Please contact support to continue.`
                }
              />
            );
          }

          return (
            <SingleChoice
              {...commonProps}
              isTreatmentJourney={isTreatmentJourney}
              question={{ ...q, answer_choices: finalChoices }}
            />
          );
        case "medication_dose_selector":
          return <MedicationDoseSelector {...commonProps} />;
        case "state_routing":
          return (
            <StateRouting
              {...commonProps}
              onChange={(value) => {
                // Treatment journeys require an explicit Continue/Confirm
                // action; legacy questionnaires retain their existing
                // auto-advance behavior.
                setCareLocationAvailability(null);
                handleResponseChange(q.id, value);
              }}
              patientInfo={authState.patient || patientInfo}
              previewStates={
                isTreatmentJourney
                  ? previewStateRoutingProjection(questionnaire)
                  : null
              }
              authoritativeAvailability={careLocationAvailability}
            />
          );
        case "medical_conditions":
        case "self_reported_meds":
        case "allergies":
          return <TextInput {...commonProps} />;
        default:
          return isTreatmentJourney
            ? <UnsupportedJourneyItem question={q} />
            : <TextInput {...commonProps} />;
      }
    },
    [
      responses,
      getQuestionValidationError,
      validationError,
      handleResponseChange,
      handleProgramIdentityChange,
      patientInfo,
      authState,
      templateId,
      clientConfig,
      effectiveClientConfig,
      visitConfig,
      handleLoginSuccess,
      questionnaire,
      updateResponse,
      couponCode,
      isPreviewMode,
      previewIdentity,
      isTreatmentJourney,
      careLocationAvailability,
      previewProgramId,
      previewCustomProgramId,
      removeQuestionnaireFile,
      uploadTreatmentRuntimeFile,
    ]
  );

  // 🚫 DISABLED: Welcome page flow no longer needed
  // const handleWelcomeStart = useCallback(() => {
  //   setCurrentFlow(FLOW_STATES.PATIENT_INFO);
  // }, [FLOW_STATES]);

  // useEffect(() => {
  //   if (
  //     questionnaire &&
  //     patientInfo &&
  //     currentFlow === FLOW_STATES.PATIENT_INFO
  //   ) {
  //     setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
  //   }
  // }, [questionnaire, patientInfo, currentFlow, FLOW_STATES]);

  useEffect(() => {
    if (questionnaireError) {
      setError(questionnaireError);
    }
  }, [questionnaireError]);

  useEffect(() => {
    if (disqualified) {
      // Preserve the exact position that produced the terminal screen. Both
      // standalone and Custom Program journeys can install a fresh server
      // projection before Back finishes, and release/local identities can
      // change during that reconciliation.
      if (
        isTreatmentJourney
        && treatmentDisqualificationIndexRef.current === null
      ) {
        treatmentDisqualificationIndexRef.current = currentQuestionIndex;
      }
      setCurrentFlow(FLOW_STATES.DISQUALIFIED);
    } else {
      treatmentDisqualificationIndexRef.current = null;
    }
  }, [
    disqualified,
    FLOW_STATES,
    currentQuestionIndex,
    isTreatmentJourney,
  ]);

  // 🚫 DISABLED: Welcome and patient info pages - now handled by backend questions
  // ===== RENDER WELCOME PAGE FLOW =====
  // if (currentFlow === FLOW_STATES.WELCOME) {
  //   return (
  //     <WelcomePage onStart={handleWelcomeStart} clientConfig={clientConfig} />
  //   );
  // }

  // ===== RENDER PATIENT INFO FLOW =====
  // if (currentFlow === FLOW_STATES.PATIENT_INFO) {
  //   return (
  //     <Container>
  //       <HeaderBar>
  //         <HeaderLeft>
  //           <Logo>
  //             <svg width="120" height="32" viewBox="0 0 120 32" fill="none">
  //               <text
  //                 x="0"
  //                 y="24"
  //                 fontFamily="var(--zyvgo-font-family)"
  //                 fontSize="24"
  //                 fontWeight="700"
  //                 fill="var(--zyvgo-primary)"
  //               >
  //                 {getCompanyName()}
  //               </text>
  //             </svg>
  //           </Logo>
  //         </HeaderLeft>
  //       </HeaderBar>

  //       <ContentWrapper>
  //         {error && <ErrorMessage>{error}</ErrorMessage>}

  //         {!loading && (
  //           <PatientInfoForm
  //             onSubmit={handlePatientInfoSubmit}
  //             loading={loading}
  //           />
  //         )}

  //         {loading && (
  //           <LoadingContainer>
  //             <LoadingSpinner text="Processing your information..." />
  //           </LoadingContainer>
  //         )}
  //       </ContentWrapper>
  //     </Container>
  //   );
  // }

  const renderLogoContent = () => {
    if (isPreviewMode && isRehydrating) {
      return <BrandPlaceholder aria-hidden="true" />;
    }
    const safeUrl =
      typeof headerLogoUrl === "string" ? headerLogoUrl.trim() : "";
    if (safeUrl && !headerLogoFailed) {
      return (
        <img
          src={safeUrl}
          alt={getCompanyName()}
          onError={() => setHeaderLogoFailed(true)}
          style={{ height: "32px", width: "auto", display: "block" }}
        />
      );
    }
    return <CompanyNameText>{getCompanyName()}</CompanyNameText>;
  };

  // ===== RENDER DISQUALIFIED FLOW =====
  if (currentFlow === FLOW_STATES.DISQUALIFIED) {
    const handleGoBackFromDisqualification = async () => {
      if (legacyAutoAdvanceTimerRef.current) {
        clearTimeout(legacyAutoAdvanceTimerRef.current);
        legacyAutoAdvanceTimerRef.current = null;
      }
      if (
        isTreatmentJourney
        && disqualificationReason?.disqualifyingCondition
          === "treatment_runtime_authority"
        && disqualificationReason?.reversible === false
      ) {
        setLoading(true);
        try {
          if (isPreviewMode) {
            await resetTreatmentPreviewAuthority();
            clearDisqualification();
            setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
          } else {
            await resetProgramPatientIdentity();
          }
        } catch (resetError) {
          setError(
            resetError.message
            || "Unable to start a new treatment assessment.",
          );
        } finally {
          setLoading(false);
        }
        return;
      }

      // Get the question ID that caused disqualification
      const questionIdToClear =
        disqualificationReason && typeof disqualificationReason === "object"
          ? disqualificationReason.questionId
          : null;

      if (
        isTreatmentJourney
        && disqualificationReason?.disqualifyingCondition
        === "treatment_runtime_authority"
      ) {
        const backState = (
          disqualificationReason.backState
          || TREATMENT_RUNTIME_STATE.routing
        );
        setLoading(true);
        try {
          await synchronizeTreatmentAuthority(
            backState,
            getResponsesSnapshot(),
            null,
            { allowBackward: true },
          );
        } catch (backError) {
          if (isRuntimePatientPermissionError(backError)) {
            const email = patientInfo?.email || authStateRef.current?.patient?.email || "";
            setLoginInitialEmail(email);
            await resetProgramPatientIdentity();
            const authenticationIndex = visibleQuestions.findIndex(
              (question) => question.question_type === "patient_authentication",
            );
            setCurrentQuestionIndex(authenticationIndex >= 0 ? authenticationIndex : 0);
            setLoading(false);
            return;
          }
          setError(
            backError.message
            || "Unable to return to the treatment questionnaire.",
          );
          setLoading(false);
          return;
        }
        setLoading(false);
        const rewindIdentity = (
          disqualificationReason.sourceQuestionId
          || disqualificationReason.questionId
        );
        rewindTreatmentResponses(rewindIdentity);
        clearDisqualification();
        setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
        // The server has already reconciled the disqualification and selected
        // the exact released node to reopen. Do not replace that cursor with
        // the browser's stale sourceQuestionId; in a Custom Program that can
        // resolve to the first sibling Program instead.
        if (
          treatmentAuthority?.presentation_contract_version === "1"
          && typeof navigateToCurrentTreatmentAuthority === "function"
          && navigateToCurrentTreatmentAuthority()
        ) {
          return;
        }
        if (
          isCustomProgramJourney
          && rewindIdentity
          && typeof navigateToStableQuestion === "function"
        ) {
          navigateToStableQuestion(
            rewindIdentity,
            backState,
          );
          return;
        }
        const capturedTargetIndex = treatmentDisqualificationIndexRef.current;
        const targetIndex = visibleQuestions.findIndex(
          (question) => (
            [question.id, question.source_id, question.release_node_id]
              .filter(Boolean)
              .some((value) => String(value) === String(rewindIdentity))
          ),
        );
        if (
          Number.isInteger(capturedTargetIndex)
          && capturedTargetIndex >= 0
          && capturedTargetIndex < visibleQuestions.length
        ) {
          setCurrentQuestionIndex(capturedTargetIndex);
        } else if (targetIndex >= 0) {
          setCurrentQuestionIndex(targetIndex);
        } else {
          const stateIndex = visibleQuestions.findIndex(
            (question) => (
              authorityStateForQuestion(question) === backState
              && question.question_type !== "personal_details"
              && question.question_type !== "patient_authentication"
            ),
          );
          setCurrentQuestionIndex(stateIndex >= 0 ? stateIndex : 0);
        }
        return;
      }

      // Clear disqualification state and the disqualifying response (e.g. DOB)
      clearDisqualification(questionIdToClear);

      // Reset to questionnaire flow
      setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);

      // Navigate directly to the question that caused disqualification.
      // We cannot use previousQuestion() here because currentQuestionIndex
      // is already pointing at the DOB question when disqualification fires —
      // calling previousQuestion() would go one step too far back (to the
      // question BEFORE DOB), which is the bug.
      if (questionIdToClear) {
        const targetIndex = visibleQuestions.findIndex(
          (q) => q.id === questionIdToClear
        );
        if (targetIndex !== -1) {
          // Directly set the index to land exactly on the DOB question
          // The response was already cleared by clearDisqualification above,
          // so the patient sees a fresh empty DOB field.
          setCurrentQuestionIndex(targetIndex);
          return;
        }
      }

      // Fallback: if we couldn't find the question, stay on current index
      // (don't call previousQuestion which would go too far back)
    };

    return (
      <Container className={isTreatmentJourney ? "treatment-questionnaire" : "legacy-questionnaire"}>
        <HeaderBar data-preview-region="header">
          <HeaderLeft>
            <Logo data-preview-region="brand">
              {renderLogoContent()}
            </Logo>
          </HeaderLeft>
        </HeaderBar>

        <DisqualificationContainer>
          <DisqualificationIcon>
            <FaInfoCircle />
          </DisqualificationIcon>
          <DisqualificationTitle>Assessment Not Eligible</DisqualificationTitle>
          <DisqualificationText>
            {typeof disqualificationReason === "object" &&
              disqualificationReason?.reason
              ? disqualificationReason.reason
              : typeof disqualificationReason === "string"
                ? disqualificationReason
                : "Based on your responses, you may not be eligible for this program at this time. Please consult with your healthcare provider for alternative options."}
          </DisqualificationText>
          <ContinueButton
            type="button"
            onClick={handleGoBackFromDisqualification}
          >
            {runtimeDisqualificationActionLabel(disqualificationReason)}
          </ContinueButton>
        </DisqualificationContainer>
      </Container>
    );
  }

  // ===== RENDER PAYMENT FLOW =====
  if (currentFlow === FLOW_STATES.PAYMENT) {
    return (
      <Container className={isTreatmentJourney ? "treatment-questionnaire" : "legacy-questionnaire"}>
        <HeaderSection>
          <ProgressBarContainer>
            <ProgressBarFill width={100} />
          </ProgressBarContainer>

          <HeaderBar data-preview-region="header">
            <HeaderLeft>
              <Logo data-preview-region="brand">
                {renderLogoContent()}
              </Logo>
            </HeaderLeft>
            <BackButton
              type="button"
              data-preview-region="back"
              onClick={
                isTreatmentJourney
                  ? handleBackFromTreatmentCheckout
                  : () => setCurrentFlow(FLOW_STATES.QUESTIONNAIRE)
              }
              disabled={loading}
              aria-label="Go back"
            >
              <HiArrowCircleLeft />
            </BackButton>
          </HeaderBar>
        </HeaderSection>

        <ContentWrapper data-preview-region="content">
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {isTreatmentJourney ? (
            <>
              <TreatmentJourneyChrome
                presentation={runtimePresentation}
                progressOverride={overallJourneyProgress}
              />
              {!treatmentOrderSummary ? (
                <ErrorCard
                  message="The immutable checkout summary is unavailable. Go back and prepare checkout again."
                  variant="error"
                />
              ) : (
                <TreatmentCheckoutReview
                  summary={treatmentOrderSummary}
                  previewMode={isPreviewMode}
                  previewDiscountRate={previewDiscountRate}
                >
                  <PaymentStep
                    ref={paymentRef}
                    onError={(e) => setPaymentError(e?.message || e)}
                    responses={responses}
                    questionnaire={questionnaire}
                    mode="checkout"
                    checkoutOrderSummary={treatmentOrderSummary}
                    hideCheckoutOrderSummary={isPreviewMode}
                    paymentAdapter={previewPaymentAdapter}
                  />

                  {paymentError && (
                    <ErrorCard message={paymentError} variant="error" />
                  )}

                  <TreatmentCheckoutRecovery
                    recovery={treatmentCheckoutRecovery}
                    treatments={treatmentCheckout?.treatments || []}
                    busy={loading}
                    message={treatmentRecoveryMessage}
                    quoteChange={treatmentQuoteChange}
                    onConfirmQuote={confirmTreatmentQuoteChange}
                    onAction={(
                      action,
                      treatmentTypeId,
                      optionId,
                      recoveryCouponCode,
                    ) => {
                      setPaymentError(null);
                      recoverTreatmentCheckoutAction(
                        action,
                        treatmentTypeId,
                        optionId,
                        recoveryCouponCode,
                      )
                        .then((result) => {
                          if (result.checkout_cancelled) {
                            setCurrentFlow(FLOW_STATES.COMPLETED);
                          } else if (
                            !result.requires_quote_confirmation &&
                            result.requires_requote
                          ) {
                            setCurrentFlow(FLOW_STATES.QUESTIONNAIRE);
                          }
                        })
                        .catch((recoveryError) => {
                          setPaymentError(
                            recoveryError.message ||
                            "Checkout recovery could not be completed."
                          );
                        });
                    }}
                  />

                  {!treatmentCheckoutRecovery && !treatmentQuoteChange && (
                    <ContinueButton
                      onClick={handlePaymentAndSubmit}
                      disabled={loading}
                      style={{
                        width: "100%",
                        borderRadius: 999,
                        marginTop: 12,
                        padding: "14px 20px",
                      }}
                    >
                      {loading
                        ? TREATMENT_CHECKOUT_COPY.processing
                        : TREATMENT_CHECKOUT_COPY.button}
                    </ContinueButton>
                  )}
                </TreatmentCheckoutReview>
              )}
            </>
          ) : (
            <div>
              <HelperText>
                Please provide your payment details to complete your assessment.
              </HelperText>
              <PaymentStep
                ref={paymentRef}
                onError={(e) => setPaymentError(e?.message || e)}
                responses={responses}
                questionnaire={questionnaire}
              />
              {paymentError && (
                <ErrorCard message={paymentError} variant="error" />
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 12,
                }}
              >
                <div style={{ width: "100%", maxWidth: 540 }}>
                  <ContinueButton
                    onClick={handlePaymentAndSubmit}
                    disabled={loading}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      padding: "14px 20px",
                    }}
                  >
                    {loading ? "Processing..." : "Complete Payment"}
                  </ContinueButton>
                </div>
              </div>
            </div>
          )}
        </ContentWrapper>
      </Container>
    );
  }

  // ===== RENDER COMPLETED FLOW =====
  if (currentFlow === FLOW_STATES.COMPLETED) {
    if (isTreatmentJourney && treatmentOrderSummary) {
      return (
        <Container className="treatment-questionnaire">
          <HeaderSection>
            <ProgressBarContainer>
              <ProgressBarFill width={100} />
            </ProgressBarContainer>
            <HeaderBar data-preview-region="header">
              <HeaderLeft>
                <Logo data-preview-region="brand">
                  {renderLogoContent()}
                </Logo>
              </HeaderLeft>
            </HeaderBar>
          </HeaderSection>
          <ContentWrapper data-preview-region="content">
            <TreatmentOrderSubmitted
              result={completionResult}
              summary={treatmentOrderSummary}
              previewDiscountRate={previewDiscountRate}
              patientOrdersUrl={treatmentOrdersUrl}
              patientName={
                patientInfo?.firstName
                || patientInfo?.first_name
                || ""
              }
            />
          </ContentWrapper>
        </Container>
      );
    }
    return (
      <Container className={isTreatmentJourney ? "treatment-questionnaire" : "legacy-questionnaire"}>
        <HeaderBar data-preview-region="header">
          <HeaderLeft>
            <Logo data-preview-region="brand">
              {renderLogoContent()}
            </Logo>
          </HeaderLeft>
        </HeaderBar>

        <CompletionContainer>
          <CompletionIcon>✓</CompletionIcon>
          <CompletionTitle>Assessment Complete!</CompletionTitle>
          <CompletionMessage>
            Thank you for completing your health assessment
            {patientInfo?.firstName ? `, ${patientInfo.firstName}` : ""}. Your
            responses have been recorded and a healthcare provider will review
            them shortly.
          </CompletionMessage>
        </CompletionContainer>
      </Container>
    );
  }

  // ===== RENDER QUESTIONNAIRE FLOW =====
  return (
    <Container className={`${isTreatmentJourney ? "treatment-questionnaire" : "legacy-questionnaire"}${currentDisplayQuestion?.question_type === "product_selection" ? " treatment-product-selection" : ""}`}>
      <HeaderSection>
        <ProgressBarContainer>
          <ProgressBarFill
            width={displayedProgress}
          />
        </ProgressBarContainer>

        <HeaderBar data-preview-region="header">
          <HeaderLeft>
            <Logo data-preview-region="brand">
              {renderLogoContent()}
            </Logo>
          </HeaderLeft>
          <BackButton
            type="button"
            data-preview-region="back"
            onClick={handlePrevious}
            disabled={
              loading ||
              !canGoBackWithinQuestionnaire
            }
            aria-label="Go back"
          >
            <HiArrowCircleLeft />
          </BackButton>
        </HeaderBar>
      </HeaderSection>

      <ContentWrapper data-preview-region="content">
        <EarlierIntakeChoice
          choice={earlierIntakeChoice}
          busy={questionnaireLoading}
          onContinue={continueEarlierTreatment}
          onStart={startRequestedTreatment}
        />

        {error && (
          isTreatmentJourney && treatmentRuntimeError ? (
            <TreatmentRuntimeNotice
              error={treatmentRuntimeError}
              busy={questionnaireLoading}
              differentAccount={differentAccount}
              onRetry={() => {
                setError(null);
                retryQuestionnaireInitialization();
              }}
              onStartOver={async () => {
                setError(null);
                if (isPreviewMode) {
                  await resetTreatmentPreviewAuthority();
                } else {
                  await resetTreatmentPatientAuthority();
                }
              }}
            >
              {treatmentRuntimeError.code === RUNTIME_PATIENT_AUTH_REQUIRED && (
                <TreatmentAuthenticationStep
                  brandName={getCompanyName()}
                  onAuthenticated={handleResumeSignIn}
                  apiBaseUrl={runtimeApiBaseUrl}
                />
              )}
            </TreatmentRuntimeNotice>
          ) : (
            <ErrorMessage>{error}</ErrorMessage>
          )
        )}

        {(questionnaireLoading || isRehydrating) && (!currentQuestion || isRehydrating) && (
          <LoadingContainer>
            <LoadingSpinner text="Loading questions..." />
          </LoadingContainer>
        )}

        {currentDisplayQuestion && !questionnaireLoading && !isRehydrating && (
          <div>
            {isTreatmentJourney
              && currentDisplayQuestion.question_type !== "patient_authentication"
              && (
              <TreatmentJourneyChrome
                presentation={runtimePresentation}
                progressOverride={overallJourneyProgress}
              />
              )}
            {!(["matched_program_selection", "treatment_screening_intro", "patient_authentication"].includes(currentDisplayQuestion.question_type)
              || activeProductSelectionGroup) && (
              <QuestionText>{currentDisplayQuestion.question_text}</QuestionText>
            )}
            {!(["matched_program_selection", "treatment_screening_intro", "patient_authentication"].includes(currentDisplayQuestion.question_type)
              || activeProductSelectionGroup) && currentDisplayQuestion.helper_text && (
              <HelperText>{currentDisplayQuestion.helper_text}</HelperText>
            )}

            {renderQuestion(currentDisplayQuestion)}

            {/* Show validation error using ErrorCard if present */}
            {validationError
              && !careLocationRejected
              && currentDisplayQuestion?.question_type !== "program_lab_checkout"
              && (
              <ErrorCard
                message={validationError}
                variant="error"
                action={blockedTreatment ? {
                  label: blockedTreatment.label,
                  onClick: async () => {
                    setValidationError(null);
                    setBlockedTreatment(null);
                    setLoading(true);
                    try {
                      // Explicit survivors. Without them the recommendations
                      // transition falls back to the session's full list and
                      // re-adds the treatment being dropped.
                      await synchronizeTreatmentAuthority(
                        TREATMENT_RUNTIME_STATE.productSelection,
                        responses,
                        null,
                        { recommendedProgramIds: blockedTreatment.programIds },
                      );
                    } catch (retryError) {
                      setValidationError(
                        retryError?.message
                          || "We could not continue without that treatment.",
                      );
                    } finally {
                      setLoading(false);
                    }
                  },
                } : null}
              />
            )}

            {/* Show Continue button — keep it available for recoverable validation
                errors so the patient can correct the answer and retry.
                For DOB questions: also show when dobError exists (button stays visible but disabled).
                Disabled only if loading, or if it's a DOB question with an active age error. */}
            {(showContinueButton || (dobError && currentDisplayQuestion?.question_type === 'date'))
              /* The authentication boundary owns its own submit button per
                 branch (Continue / Log In / Create Account), so the journey's
                 shared Continue must not also render. */
              && currentDisplayQuestion?.question_type !== 'patient_authentication'
              && (currentDisplayQuestion?.question_type !== 'product_selection' || hasProductsRef.current) && (
              <ContinueButton
                onClick={handleNext}
                disabled={
                  loading
                  || (!!dobError && currentDisplayQuestion?.question_type === 'date')
                  || (
                    currentDisplayQuestion?.question_type === "product_selection"
                    && !isProductSelectionComplete(
                      currentDisplayQuestion,
                      currentDisplayResponse,
                    )
                  )
                  || (
                    isTreatmentJourney
                    && currentDisplayQuestion?.question_type === "state_routing"
                    && !normalizeStateCode(currentDisplayResponse)
                    || careLocationRejected
                  )
                }
              >
                {loading
                  ? "Processing..."
                  : currentDisplayQuestion?.question_type === "personal_details"
                    ? authState.authenticated ? "Continue" : "Create Account"
                    : isTreatmentJourney && currentDisplayQuestion?.question_type === "product_selection"
                      ? "Continue"
                    : isTreatmentJourney && currentDisplayQuestion?.question_type === "state_routing"
                      ? "Continue"
                    : isLastQuestion()
                      ? "Submit"
                      : "Continue"}
              </ContinueButton>
            )}

            {/* Login Link for Personal Details */}
            {currentQuestion?.question_type === "personal_details" &&
              !authState.authenticated &&
              !loading && (
                <LoginLinkContainer>
                  Already have an account?
                  <LoginLink
                    type="button"
                    onClick={() => setShowLoginModal(true)}
                  >
                    Log in
                  </LoginLink>
                </LoginLinkContainer>
              )}
          </div>
        )}

        {/* 🚫 PAYMENT DISABLED: Commented out payment UI */}
        {/* {showPayment && (
          <div>
            <PaymentStep ref={paymentRef} onError={(e) => setPaymentError(e?.message || e)} />

            {paymentError && <ErrorCard message={paymentError} variant="error" />}

            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'center' }}>
              <button
                onClick={() => setShowPayment(false)}
                style={{ padding: '12px 24px', borderRadius: 8, border: '1px solid #ccc', background: '#fff' }}
                disabled={loading}
              >
                Back
              </button>
              <ContinueButton onClick={handlePaymentAndSubmit} disabled={loading}>
                {loading ? 'Processing...' : 'Pay & Submit'}
              </ContinueButton>
            </div>
          </div>
        )} */}

        {!currentQuestion && !questionnaireLoading && !isRehydrating && questionnaire && (
          <DisqualificationText
            style={{ textAlign: "center", marginTop: "60px" }}
          >
            No questions available. Please contact support.
          </DisqualificationText>
        )}
      </ContentWrapper>

      {addressSuggestionPrompt && (
        <AddressSuggestionDialog
          suggestion={addressSuggestionPrompt}
          onKeepEntered={() => resolveAddressSuggestionChoice(false)}
          onUseSuggested={() => resolveAddressSuggestionChoice(true)}
        />
      )}

      {showLoginModal && !isTreatmentJourney && (
        <ExistingPatientLoginModal
          templateId={templateId}
          initialEmail={loginInitialEmail}
          portalUrl={clientConfig?.patient_portal_domain || ""}
          onSuccess={async (authData) => {
            await handleLoginSuccess(authData);
            setShowLoginModal(false);
          }}
          onClose={() => setShowLoginModal(false)}
          onContinueAsNew={() => {
            setShowLoginModal(false);
            if (
              isPreviewMode &&
              previewIdentity === PREVIEW_PROTOCOL.identities.existingPatient
            ) {
              const url = new URL(window.location.href);
              url.searchParams.set(
                "preview_identity",
                PREVIEW_PROTOCOL.identities.newPatient,
              );
              window.history.replaceState({}, "", url);
              window.dispatchEvent(
                new CustomEvent(PREVIEW_PROTOCOL.events.identityChanged, {
                  detail: { source: "local", requestId: null },
                }),
              );
            }
          }}
          login={isPreviewMode
            ? async (email) => {
                const patient = await journeyBoundary.identityAdapter.resolve({
                  email,
                  ...(isPreviewMode ? previewPatientDefaults(questionnaire) : {}),
                });
                return {
                  sessionId: "preview-session",
                  accessToken: null,
                  prefillData: createPreviewPatientPrefill(patient),
                  patient,
                  template: null,
                };
              }
            : undefined}
        />
      )}
    </Container>
  );
};

export default QuestionnaireForm;
